import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppStore } from '@/store';
import * as api from '@/api/client';
import type { Claim, ClaimAnalysis, MatterFullState, Stage } from '@/types';
import { STAGE_ORDER } from '@/types';
import {
  RefreshCw, Lock, Maximize2, Minimize2,
  Loader2, ArrowRight, AlertTriangle, CheckCircle, Zap, Hash, Eye,
  History as HistoryIcon, RotateCcw, Check, CircleDot,
  PanelLeftClose, PanelLeftOpen, Plus, Trash2, X,
  ChevronDown, ChevronRight, Compass,
} from 'lucide-react';
import { ClaimDiffPanel } from '@/components/stages/ClaimDiffPanel';
import ClaimArchitecturePanel from '@/components/stages/ClaimArchitecturePanel';
import {
  evaluateClaimHealth, idfTextsFromState, computeHighlightRanges, findAntecedentGaps,
  type HealthResult, type HealthIssue,
} from '@/lib/claimHealth';
import StageGateBanner from '@/components/layout/StageGateBanner';
import StageAcknowledgment from '@/components/workspace/StageAcknowledgment';

import { apiErrorText } from '../../lib/describeApiError';
/**
 * Left rail for Stage 3.
 *
 * Matches the PRD v3 planned UI: a STAGES section that mirrors the top-bar
 * progress (with status text — "Frozen" / "In progress" / "Locked") plus a
 * CLAIMS tree where dependents are rendered nested under their independent
 * parent with a vertical connector.
 */
/** The confirmation shown before a delete that takes other claims with
 *  it or rewrites their references. ``deleted``: claims left depending
 *  on nothing, removed too. ``amended``: claims that also depend on a
 *  surviving claim and only lose the reference. */
export function deletePlanConfirmText(
  claimNumber: number,
  deleted: Array<{ claim_number: number }>,
  amended: Array<{ claim_number: number; new_text?: string; dangling?: number[] }>,
): string {
  const list = (xs: Array<{ claim_number: number }>) => {
    const nums = xs.map((x) => String(x.claim_number));
    return nums.length === 1 ? nums[0] : `${nums.slice(0, -1).join(', ')} and ${nums[nums.length - 1]}`;
  };
  const lines = [`Delete claim ${claimNumber}?`];
  if (deleted.length > 0) {
    lines.push(
      `Also DELETED: claim${deleted.length === 1 ? '' : 's'} ${list(deleted)} `
      + `(${deleted.length === 1 ? 'it depends' : 'they depend'} on nothing else).`,
    );
  }
  if (amended.length > 0) {
    lines.push(
      `Kept, with references to deleted claims removed: `
      + `claim${amended.length === 1 ? '' : 's'} ${list(amended)}.`,
    );
    const dangling = amended.filter((a) => (a.dangling || []).length > 0);
    if (dangling.length > 0) {
      lines.push(
        `Claim${dangling.length === 1 ? '' : 's'} ${list(dangling)} `
        + 'will get a SPECIFY placeholder where the reference was.',
      );
    }
  }
  lines.push('The remaining claims are renumbered.');
  return lines.join('\n\n');
}

/** Why Run QC cannot run yet, or null when it can. QC reads the frozen
 *  claim set and the server refuses it (422 stage_3_not_frozen) until
 *  Stage 3 is frozen with Continue to QC. */
export function qcRunBlockedReason(stage3Frozen: boolean, claimCount: number): string | null {
  if (claimCount === 0) return 'Generate claims before running QC.';
  if (!stage3Frozen) {
    return 'QC runs on the frozen claim set. Click Continue to QC to freeze Stage 3, then click Run QC in Stage 4.';
  }
  return null;
}

/** "claim 1", "claims 1 and 6", "claims 1, 4 and 6" with each number a
 *  link to its card. */
function ClaimNumberLinks({ claims, onJump }: {
  claims: Claim[]; onJump: (claimId: string) => void;
}) {
  const sorted = [...claims].sort((a, b) => a.claim_number - b.claim_number);
  return (
    <>
      {sorted.length === 1 ? 'claim ' : 'claims '}
      {sorted.map((c, i) => (
        <React.Fragment key={c.id}>
          {i > 0 && (i === sorted.length - 1 ? ' and ' : ', ')}
          <button
            type="button"
            data-testid={`stage-3-gate-claim-${c.claim_number}`}
            onClick={() => onJump(c.id)}
            className="font-medium underline underline-offset-2 hover:text-amber-950"
          >
            {c.claim_number}
          </button>
        </React.Fragment>
      ))}
    </>
  );
}

/** The Stage 3 gate banner text. Names the claims to freeze and the
 *  claims holding placeholders, each a link to its card: a count alone
 *  left drafters hunting through the set. */
export function ClaimGateMessage({ unfrozen, withPlaceholders, onJump }: {
  unfrozen: Claim[];
  withPlaceholders: Claim[];
  onJump: (claimId: string) => void;
}) {
  const placeholders = withPlaceholders.reduce((n, c) => n + c.placeholder_count, 0);
  return (
    <>
      Before advancing to QC,
      {unfrozen.length > 0 && (
        <> freeze <ClaimNumberLinks claims={unfrozen} onJump={onJump} /></>
      )}
      {unfrozen.length > 0 && placeholders > 0 && ' and'}
      {placeholders > 0 && (
        <>
          {' '}resolve {placeholders === 1 ? 'the placeholder' : `the ${placeholders} placeholders`} in{' '}
          <ClaimNumberLinks claims={withPlaceholders} onJump={onJump} />
        </>
      )}
      .
    </>
  );
}

/** The Freeze button's hover text: why it is greyed out, or what it
 *  does. A disabled button with no reason looked broken to drafters. */
export function freezeButtonTitle(placeholderCount: number): string {
  if (placeholderCount > 0) {
    return placeholderCount === 1
      ? 'Replace the placeholder (the yellow \u27e6SPECIFY: \u2026\u27e7 text) in this claim before freezing it.'
      : `Replace the ${placeholderCount} placeholders (the yellow \u27e6SPECIFY: \u2026\u27e7 text) in this claim before freezing it.`;
  }
  return 'Freeze this claim. Continue to QC needs every independent claim frozen.';
}

/** Why Steer is disabled while a proposed change is waiting, or null.
 *  The proposal renders at the bottom of the card, so without a reason
 *  the button just looked dead. */
export function steerDisabledReason(pendingRegen: unknown, pendingSteer: unknown): string | null {
  if (pendingRegen || pendingSteer) {
    return 'Accept or discard the proposed change below before steering this claim';
  }
  return null;
}

/** A claim's direct parents: parent_claim_numbers when the backend sent
 *  them (EP multiple dependency), else parent_claim_number. Unique and
 *  ascending; empty for an independent or an unlinked dependent. */
export function claimParentNumbers(
  claim: Pick<Claim, 'parent_claim_number' | 'parent_claim_numbers'>,
): number[] {
  const listed = claim.parent_claim_numbers && claim.parent_claim_numbers.length > 0
    ? claim.parent_claim_numbers
    : [claim.parent_claim_number];
  const nums = listed.filter((n): n is number => n != null);
  return Array.from(new Set(nums)).sort((a, b) => a - b);
}

/** Parent claims the way a claim would cite them: "claim 1", "claim 1
 *  or 2", "claims 1 to 5", "claim 1, 3 or 5". Takes ascending numbers,
 *  as claimParentNumbers returns them. */
export function formatParentClaims(nums: number[]): string {
  if (nums.length === 0) return '';
  if (nums.length === 1) return `claim ${nums[0]}`;
  const last = nums[nums.length - 1];
  if (nums.length > 2 && nums.every((n, i) => i === 0 || n === nums[i - 1] + 1)) {
    return `claims ${nums[0]} to ${last}`;
  }
  return `claim ${nums.slice(0, -1).join(', ')} or ${last}`;
}

/** The type chip on a claim card. "Dependent" alone did not say on
 *  what; a dependent names its own parents, never the root independent
 *  the navigator files it under. */
export function claimTypeLabel(claim: Claim): string {
  if (claim.claim_type === 'dependent') {
    const parents = claimParentNumbers(claim);
    return parents.length > 0 ? `Dependent on ${formatParentClaims(parents)}` : 'Dependent';
  }
  return claim.claim_type.charAt(0).toUpperCase() + claim.claim_type.slice(1);
}

/** How many of a card's analysis boxes hold something. Dependents have
 *  no Distinguishing Features box, so it is not counted for them. */
export function analysisFill(
  analysis: ClaimAnalysis | null | undefined,
  independent: boolean,
): { filled: number; total: number } {
  const a = analysis || {};
  const has = (v: unknown) => (Array.isArray(v)
    ? v.some((t) => typeof t === 'string' && t.trim() !== '')
    : typeof v === 'string' && v.trim() !== '');
  const fields: unknown[] = [a.key_terms, a.technical_effect, a.advantage];
  if (independent) fields.unshift(a.distinguishing_features);
  return { filled: fields.filter(has).length, total: fields.length };
}

/** Whether a card opens with Analysis expanded. A dependent's analysis
 *  is mostly empty, and an empty one says nothing until the drafter
 *  opens it to fill it in. */
export function analysisStartsOpen(claim: Claim): boolean {
  return claim.claim_type !== 'dependent' && analysisFill(claim.analysis, true).filled > 0;
}

export interface SteerAntecedentBreak {
  claim_number: number;
  /** The references as written, e.g. "the forward deceleration". */
  texts: string[];
}

/** Claims below ``claimId`` (directly or through other dependents) that
 *  would refer to wording nothing introduces once ``newText`` replaces
 *  its text and the ticked dependent rewrites in ``appliedDependents``
 *  are applied. Only gaps the change creates are reported; one that is
 *  already there is the health check's business. */
export function antecedentBreaksAfterSteer(
  claimId: string,
  newText: string,
  claims: Claim[],
  appliedDependents: Array<{ id: string; suggested_text: string }> = [],
): SteerAntecedentBreak[] {
  const steered = claims.find((c) => c.id === claimId);
  if (!steered) return [];
  const rewrites = new Map(appliedDependents.map((d) => [d.id, d.suggested_text]));
  rewrites.set(claimId, newText);
  const after = claims.map((c) => {
    const text = rewrites.get(c.id);
    return text === undefined ? c : { ...c, claim_text: text };
  });

  // Grow the set to a fixed point; it only grows, so a cycle ends it.
  const below = new Set([steered.claim_number]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of claims) {
      if (!below.has(c.claim_number) && claimParentNumbers(c).some((p) => below.has(p))) {
        below.add(c.claim_number);
        grew = true;
      }
    }
  }
  below.delete(steered.claim_number);

  const out: SteerAntecedentBreak[] = [];
  claims.forEach((c, i) => {
    if (!below.has(c.claim_number)) return;
    const before = new Set(findAntecedentGaps(c, claims).map((g) => g.phrase));
    const texts = findAntecedentGaps(after[i], after)
      .filter((g) => !before.has(g.phrase))
      .map((g) => g.text);
    if (texts.length > 0) out.push({ claim_number: c.claim_number, texts });
  });
  return out.sort((a, b) => a.claim_number - b.claim_number);
}

/** The note under a steer proposal naming the dependents it would leave
 *  without an antecedent, or null. ``tickable``: claim numbers whose
 *  suggested update is offered and not yet ticked. */
export function steerAntecedentNote(
  claimNumber: number,
  breaks: SteerAntecedentBreak[],
  tickable: number[],
): string | null {
  if (breaks.length === 0) return null;
  const shown = breaks.slice(0, 4).map(
    (b) => `claim ${b.claim_number} (${b.texts.map((t) => `'${t}'`).join(', ')})`,
  );
  if (breaks.length > shown.length) shown.push(`${breaks.length - shown.length} more`);
  const list = shown.length === 1
    ? shown[0]
    : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
  const one = breaks.length === 1;
  const offered = breaks.filter((b) => tickable.includes(b.claim_number)).length;
  const fix = offered === 0
    ? `Edit ${one ? 'it' : 'them'} after applying.`
    : offered === breaks.length
      ? `Tick ${one ? 'its suggested update' : 'their suggested updates'}, `
        + `or edit ${one ? 'it' : 'them'} after applying.`
      : 'Tick the suggested updates offered above, or edit them after applying.';
  return `After this change, ${list} ${one ? 'refers' : 'refer'} to wording `
    + `claim ${claimNumber} no longer introduces. ${fix}`;
}

export function Stage3Sidebar({
  claims, activeClaimId, onSelect, matterState, onJumpToStage,
  collapsed, onToggleCollapse, onAddClaimFor, readOnly, healthByClaimId,
  qcFindingsByClaimNumber, onRelink, relinkUnresolvable,
}: {
  claims: Claim[];
  activeClaimId: string | null;
  onSelect: (id: string) => void;
  matterState: MatterFullState | null;
  onJumpToStage: (s: Stage) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onAddClaimFor: (preset: { kind: 'independent' | 'dependent'; parent_claim_number: number | null }) => void;
  readOnly: boolean;
  healthByClaimId: Map<string, HealthResult>;
  qcFindingsByClaimNumber: Map<number, any[]>;
  /** Triggered from the Unlinked Dependents section — re-parses
   *  every claim's text server-side via /claims/relink and refreshes
   *  the matter so the orphan rows disappear when their parents are
   *  picked up. */
  onRelink?: () => Promise<void>;
  /** Populated by the latest /claims/relink response. Surfaces
   *  claims whose prose references a non-existent claim number —
   *  the drafter has to edit the text to fix; clicking Re-link
   *  again won't help. */
  relinkUnresolvable?: import('@/api/client').RelinkUnresolvable[];
}) {
  // ── Hooks must run unconditionally on every render ──────────────
  // Local busy flag for the Re-link Dependencies button rendered
  // inside the Unlinked Dependents section below. Declared at the
  // very top of the component so the rules-of-hooks invariant holds
  // — the early-return for the collapsed view (below) used to sit
  // ahead of this useState, which short-circuited the hook on every
  // collapsed render and produced the classic "Rendered fewer hooks
  // than expected" React warning the moment the drafter expanded
  // the rail.
  const [relinking, setRelinking] = useState(false);

  // Collapsed view: thin rail with only the expand toggle. Gives the drafter
  // the full page width for the claim card (e.g., when reading a long claim
  // text with many placeholders).
  if (collapsed) {
    return (
      <div
        className="w-10 border-r border-zinc-200 bg-zinc-50 flex flex-col items-center pt-4 pb-6 flex-shrink-0"
        data-testid="claim-navigator-collapsed"
      >
        <button
          type="button"
          data-testid="sidebar-expand-button"
          onClick={onToggleCollapse}
          title="Expand sidebar"
          className="p-1.5 rounded-sm text-zinc-500 hover:text-[#002FA7] hover:bg-white transition-colors"
        >
          <PanelLeftOpen size={16} />
        </button>
      </div>
    );
  }
  const currentStage = matterState?.matter?.current_stage;
  const currentIdx = currentStage ? STAGE_ORDER.indexOf(currentStage) : -1;

  // Short labels for the STAGES list — the long-form labels in STAGE_LABELS
  // are tuned for the top bar; in the sidebar we mirror the planned UI.
  const stageMeta: Record<Stage, string> = {
    stage_0: 'Upload',
    stage_1: 'IDF understanding',
    stage_2: 'Novelty & IS',
    stage_2_5: 'Claim strategy',
    stage_3: 'Claim drafting',
    stage_4: 'QC & export',
  };

  const stageStatus = (stage: Stage): { label: string; accessible: boolean } => {
    const idx = STAGE_ORDER.indexOf(stage);
    const sstate = matterState?.stages?.[stage];
    const frozen = !!sstate?.is_frozen;
    if (frozen) {
      // Stage 2.5 uses "Added & locked" when the drafter saved a strategy;
      // "Skipped" when they chose to skip. Falls back to "Locked" if unset.
      if (stage === 'stage_2_5') {
        const hasStrategy = !!(sstate?.content?.strategy_text || '').trim();
        return { label: hasStrategy ? 'Added & locked' : 'Skipped', accessible: true };
      }
      return { label: 'Frozen', accessible: true };
    }
    if (idx === currentIdx) return { label: 'In progress', accessible: true };
    if (idx < currentIdx) return { label: 'Complete', accessible: true };
    return { label: 'Locked', accessible: false };
  };

  // Build the parent → dependents map. A claim is "orphaned" (and
  // surfaced in the UNLINKED DEPENDENTS section) ONLY when its
  // parent_claim_number references a claim_number that does not
  // exist on this matter. Previously the predicate insisted the
  // parent be an independent, which mis-bucketed every legitimate
  // dep-of-dep (Claim 3 depending on dependent Claim 2 etc.) as an
  // orphan even though the chain was perfectly valid. The fix
  // walks parent_claim_number up to the first independent ancestor
  // and groups the dependent under THAT independent, so the
  // sidebar tree shows dep-chains flat under their root indep
  // without false-orphan noise.
  const independents = claims.filter(c => c.claim_type === 'independent');
  const claimByNumber = new Map<number, Claim>();
  for (const c of claims) claimByNumber.set(c.claim_number, c);

  const dependentsByParent: Record<number, Claim[]> = {};
  const orphanDeps: Claim[] = [];
  for (const c of claims) {
    if (c.claim_type !== 'dependent') continue;
    const directParent = c.parent_claim_number;
    if (!directParent || !claimByNumber.has(directParent)) {
      // Direct parent_claim_number names a claim that simply isn't
      // on this matter — the genuine orphan case the section was
      // designed for.
      orphanDeps.push(c);
      continue;
    }
    // Walk up to the first independent ancestor. Guard against
    // cycles (a misconfigured matter could in principle land a
    // dependent pointing at itself transitively) by capping the
    // walk at the total claim count.
    let cursor: Claim | undefined = c;
    let rootIndep: Claim | null = null;
    const visited = new Set<string>();
    for (let i = 0; i < claims.length && cursor; i++) {
      if (visited.has(cursor.id)) break;
      visited.add(cursor.id);
      if (cursor.claim_type === 'independent') {
        rootIndep = cursor;
        break;
      }
      const pn = cursor.parent_claim_number;
      cursor = pn ? claimByNumber.get(pn) : undefined;
    }
    if (rootIndep) {
      (dependentsByParent[rootIndep.claim_number] ||= []).push(c);
    } else {
      // Chain didn't terminate at an independent — also an orphan,
      // surfaced so the drafter can fix the dangling reference.
      orphanDeps.push(c);
    }
  }
  for (const deps of Object.values(dependentsByParent)) {
    deps.sort((a, b) => a.claim_number - b.claim_number);
  }

  return (
    <div
      className="w-72 border-r border-zinc-200 bg-zinc-50 overflow-y-auto flex-shrink-0 pt-4 pb-6"
      data-testid="claim-navigator"
    >
      {/* Collapse toggle — sits in the sidebar header so drafters can
          reclaim the horizontal space when reading a long claim. */}
      <div className="flex items-center justify-between px-4 mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Navigator</span>
        <button
          type="button"
          data-testid="sidebar-collapse-button"
          onClick={onToggleCollapse}
          title="Collapse sidebar"
          className="p-1 rounded-sm text-zinc-500 hover:text-[#002FA7] hover:bg-white transition-colors"
        >
          <PanelLeftClose size={14} />
        </button>
      </div>

      {/* ── STAGES ── */}
      <h3 className="px-4 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Stages</h3>
      <div className="space-y-0.5 mb-6">
        {STAGE_ORDER.map(stage => {
          const { label, accessible } = stageStatus(stage);
          const isCurrent = stage === 'stage_3';
          const sstate = matterState?.stages?.[stage];
          const frozen = !!sstate?.is_frozen;
          // Icon: check for frozen/complete, filled dot for in-progress,
          // padlock for locked.
          const icon = frozen
            ? <Check size={12} className="text-green-600" />
            : isCurrent
              ? <CircleDot size={12} className="text-[#002FA7]" />
              : accessible
                ? <CircleDot size={12} className="text-zinc-400" />
                : <Lock size={12} className="text-zinc-400" />;
          return (
            <button
              key={stage}
              type="button"
              data-testid={`sidebar-stage-${stage}`}
              onClick={() => accessible && onJumpToStage(stage)}
              disabled={!accessible}
              className={`w-full px-4 py-1.5 text-left text-sm flex items-center gap-2 transition-colors ${
                isCurrent
                  ? 'bg-white border-r-2 border-[#002FA7] text-zinc-900'
                  : accessible
                    ? 'text-zinc-600 hover:bg-white/70'
                    : 'text-zinc-400 cursor-not-allowed'
              }`}
            >
              <span className="flex-shrink-0">{icon}</span>
              <span className="truncate flex-1">{stageMeta[stage]}</span>
              <span className={`text-[10px] font-mono uppercase tracking-wide flex-shrink-0 ${
                frozen ? 'text-green-700'
                  : isCurrent ? 'text-[#002FA7]'
                  : accessible ? 'text-zinc-400'
                  : 'text-zinc-300'
              }`}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* ── CLAIMS tree ── */}
      <h3 className="px-4 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Claims</h3>
      <div>
        {independents.length === 0 && orphanDeps.length === 0 && (
          <div className="px-4 py-2 text-xs text-zinc-400">No claims yet.</div>
        )}

        {independents.map(ind => {
          const deps = dependentsByParent[ind.claim_number] || [];
          return (
            <div key={ind.id} className="mb-1">
              <ClaimRow
                claim={ind}
                activeClaimId={activeClaimId}
                onSelect={onSelect}
                health={healthByClaimId.get(ind.id) || null}
                qcCount={(qcFindingsByClaimNumber.get(ind.claim_number) || []).length}
                needsFreeze
              />
              {/* Tree connector + dependents + "+ Add dependent" at the
                  bottom of each subtree. */}
              <div className={`ml-5 ${deps.length > 0 ? 'border-l border-zinc-300' : ''} pl-0 mt-0.5`}>
                {deps.map(dep => (
                  <ClaimRow
                    key={dep.id}
                    claim={dep}
                    activeClaimId={activeClaimId}
                    onSelect={onSelect}
                    parentNumber={ind.claim_number}
                    health={healthByClaimId.get(dep.id) || null}
                    qcCount={(qcFindingsByClaimNumber.get(dep.claim_number) || []).length}
                  />
                ))}
                {!readOnly && (
                  <button
                    type="button"
                    data-testid={`add-dependent-to-${ind.claim_number}`}
                    onClick={() => onAddClaimFor({ kind: 'dependent', parent_claim_number: ind.claim_number })}
                    className="w-full px-3 py-1 text-left text-[11px] text-zinc-400 hover:text-[#002FA7] hover:bg-white/70 flex items-center gap-1 transition-colors"
                  >
                    <Plus size={11} /> Add dependent of claim {ind.claim_number}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {orphanDeps.length > 0 && (
          <div className="mt-2 border-t border-zinc-200 pt-2">
            <div className="px-4 text-[10px] font-mono uppercase tracking-wide text-zinc-400 mb-1">
              Unlinked dependents
            </div>
            {orphanDeps.map(dep => (
              <ClaimRow
                key={dep.id}
                claim={dep}
                activeClaimId={activeClaimId}
                onSelect={onSelect}
                health={healthByClaimId.get(dep.id) || null}
                qcCount={(qcFindingsByClaimNumber.get(dep.claim_number) || []).length}
              />
            ))}
            {/* Re-parse claim text server-side and rewrite
                depends_on_ids. Visible only when there ARE
                orphans (the section above gates on that). When
                the parse picks up parents that the post-generate
                relink missed (e.g. a partial-JSON recovery that
                landed mid-claim, or a freeform edit that changed
                the parent number) the orphan rows disappear on
                the next refresh. */}
            {!readOnly && onRelink && (
              <button
                type="button"
                data-testid="relink-dependencies-button"
                onClick={async () => {
                  if (relinking) return;
                  setRelinking(true);
                  try { await onRelink(); }
                  finally { setRelinking(false); }
                }}
                disabled={relinking}
                title="Re-parse claim text to resolve parent links"
                className="mt-1 w-full px-4 py-1.5 text-left text-xs text-[#002FA7] hover:bg-white/70 flex items-center gap-1.5 transition-colors disabled:opacity-50"
              >
                {relinking
                  ? <Loader2 size={12} className="animate-spin" />
                  : <RotateCcw size={12} />}
                {relinking ? 'Re-linking…' : 'Re-link Dependencies'}
              </button>
            )}
            {relinkUnresolvable && relinkUnresolvable.length > 0 && (
              <div
                data-testid="relink-unresolvable"
                className="mt-2 mx-4 p-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded"
              >
                <div className="font-medium mb-1">
                  {relinkUnresolvable.length} claim
                  {relinkUnresolvable.length === 1 ? '' : 's'}
                  {' still unlinked — the claim text references a '}
                  claim number that does not exist on this matter.
                  Edit the claim text to fix the reference.
                </div>
                <ul className="mt-1 space-y-0.5">
                  {relinkUnresolvable.map(u => (
                    <li
                      key={u.claim_id}
                      className="font-mono text-[11px] text-amber-800"
                    >
                      Claim {u.claim_number}: references claim{' '}
                      {u.parent_claim_number ?? '?'} which does not exist
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!readOnly && (
          <div className="mt-3 border-t border-zinc-200 pt-2">
            <button
              type="button"
              data-testid="add-independent-claim"
              onClick={() => onAddClaimFor({ kind: 'independent', parent_claim_number: null })}
              className="w-full px-4 py-1.5 text-left text-xs text-[#002FA7] hover:bg-white/70 flex items-center gap-1.5 transition-colors"
            >
              <Plus size={12} /> Add independent claim
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Named props type — using a named type (instead of an inline object type in
// the function signature) keeps React's intrinsic `key` attribute handling
// working when the component is rendered inside a .map(). With the inline
// object type, strict TS flags `key` as an "unknown prop".
// Extracted component type so React.FC adds the implicit JSX intrinsic
// attributes (`key`, `ref`) — TS strict mode rejects `<ClaimRow key=… />`
// when the component uses a plain destructured-parameter type.
type ClaimRowProps = {
  claim: Claim;
  activeClaimId: string | null;
  onSelect: (id: string) => void;
  /** The root independent the row is filed under. Labels a dependent
   *  only when the claim itself carries no parent. */
  parentNumber?: number;
  health?: HealthResult | null;
  /** Number of QC findings referencing this claim_number — when > 0
   *  a red pip renders on the row so the drafter can see which
   *  claims need attention without opening every card. */
  qcCount?: number;
  /** When true, this row is an unfrozen independent claim that the
   *  Continue-to-QC gate is specifically blocking on. Used to draw
   *  a subtle "needs freeze" marker next to the row so the tree
   *  aligns with the blocked-reason banner at the top of Stage 3. */
  needsFreeze?: boolean;
};

const ClaimRow: React.FC<ClaimRowProps> = ({ claim, activeClaimId, onSelect, parentNumber, health, qcCount, needsFreeze }) => {
  const isActive = claim.id === activeClaimId;
  const isDep = claim.claim_type === 'dependent';
  // Label matches the planned UI:
  //   Independent  →  "Claim N — Method (Ind.)"
  //   Dependent    →  "Claim N — Dep. of M" (several parents: "Dep. of 1, 2")
  const categoryLabel = claim.category === 'crm'
    ? 'CRM'
    : claim.category.charAt(0).toUpperCase() + claim.category.slice(1);
  // A dependent is labelled with its own direct parents, not the root
  // independent it is filed under, so the row agrees with the card header
  // and the claim text (CS-23).
  const parentNumbers = claimParentNumbers(claim);
  const depLabelSuffix = parentNumbers.length > 0
    ? parentNumbers.join(', ')
    : `${parentNumber ?? '?'}`;
  const label = isDep
    ? `Claim ${claim.claim_number} — Dep. of ${depLabelSuffix}`
    : `Claim ${claim.claim_number} — ${categoryLabel} (Ind.)`;
  // Dependents get a slight left-padding inside the tree-connector column;
  // independents sit flush with the rail margin.
  const rowPad = isDep ? 'pl-3 pr-4' : 'px-4';
  // Health pip — green tick when all checks pass, yellow warning when
  // any health check flags an issue. Tooltip lists the flagged checks
  // so the drafter can see why without opening the card.
  const healthTooltip = health && health.status === 'warn'
    ? health.issues.map((i) => `• ${i.message}`).join('\n')
    : 'No issues found';
  return (
    <button
      type="button"
      data-testid={`claim-nav-${claim.claim_number}`}
      onClick={() => onSelect(claim.id)}
      className={`w-full ${rowPad} py-1.5 text-left text-sm flex items-center gap-2 transition-colors ${
        isActive ? 'bg-white border-r-2 border-[#002FA7] text-zinc-900' : 'text-zinc-600 hover:bg-white/70'
      }`}
    >
      {isDep && <span className="text-zinc-300 font-mono text-xs select-none">─</span>}
      <span className="truncate flex-1">{label}</span>
      {health && health.status === 'ok' && (
        <span
          data-testid={`claim-nav-health-ok-${claim.claim_number}`}
          title={healthTooltip}
          className="flex-shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full bg-green-100 text-green-700"
        >
          <Check size={10} />
        </span>
      )}
      {health && health.status === 'warn' && (
        <span
          data-testid={`claim-nav-health-warn-${claim.claim_number}`}
          title={healthTooltip}
          className="flex-shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-100 text-amber-700"
        >
          <AlertTriangle size={10} />
        </span>
      )}
      {typeof qcCount === 'number' && qcCount > 0 && (
        <span
          data-testid={`claim-nav-qc-${claim.claim_number}`}
          title={`${qcCount} QC finding${qcCount === 1 ? '' : 's'} — see claim card`}
          className="flex-shrink-0 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-100 text-red-700 text-[10px] font-mono font-semibold"
        >
          {qcCount}
        </span>
      )}
      {needsFreeze && !claim.is_frozen && (
        <span
          data-testid={`claim-nav-needs-freeze-${claim.claim_number}`}
          title="Independent claim not frozen — required before Continue to QC"
          className="flex-shrink-0 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-amber-100 text-amber-800 text-[9px] font-mono uppercase tracking-wider"
        >
          Unfrozen
        </span>
      )}
      {claim.is_frozen && <Lock size={10} className="text-green-600 flex-shrink-0" />}
      {claim.placeholder_count > 0 && (
        <span className="flex-shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-sm font-mono">
          {claim.placeholder_count}
        </span>
      )}
    </button>
  );
};

// Placeholder rendering lives in ClaimBodyRenderer below, which also
// weaves in health-check highlights. The old standalone
// PlaceholderRenderer has been retired — its chip component
// (PlaceholderChip) is still used by ClaimBodyRenderer.

function PlaceholderChip({ hint, fullMatch, onReplace, disabled }: {
  hint: string; fullMatch: string;
  onReplace: (original: string, replacement: string) => void;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  if (editing && !disabled) {
    return (
      // Clicks here must not reach the claim body, which opens the
      // whole-claim editor on click.
      <span className="inline-flex items-center gap-1 mx-1" onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value.trim()) {
              onReplace(fullMatch, value.trim());
              setEditing(false);
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          className="px-2 py-0.5 border border-amber-400 rounded-sm text-sm bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-500 w-48"
          placeholder={hint}
        />
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid="placeholder-chip"
      onClick={(e) => {
        // Edit just the placeholder; the click bubbling to the claim
        // body opened the whole-claim editor instead.
        e.stopPropagation();
        if (!disabled) setEditing(true);
      }}
      className="inline-block bg-yellow-100 border border-yellow-300 text-yellow-900 px-1.5 py-0.5 rounded-sm font-mono text-sm cursor-pointer hover:bg-yellow-200 transition-colors mx-0.5"
      title={`Click to specify: ${hint}`}
    >
      {fullMatch}
    </button>
  );
}

/**
 * Renders a claim body with both ``SPECIFY`` placeholder chips (yellow
 * clickable chip — editable) and flagged health issues (yellow ``<mark>``
 * span — informational, hover shows the message). The SPECIFY token
 * always wins on overlap: the chip is part of the editing flow, while
 * health highlights are advisory.
 */
function ClaimBodyRenderer({
  text, issues, onReplace, disabled,
}: {
  text: string;
  issues: HealthIssue[];
  onReplace: (original: string, replacement: string) => void;
  disabled: boolean;
}) {
  // 1. Find every SPECIFY placeholder range.
  const placeholderRanges: Array<{ start: number; end: number; full: string; hint: string }> = [];
  const specifyRe = /\u27e6SPECIFY:\s*([^\u27e7]+)\u27e7/g;
  let m: RegExpExecArray | null;
  while ((m = specifyRe.exec(text)) !== null) {
    placeholderRanges.push({
      start: m.index,
      end: m.index + m[0].length,
      full: m[0],
      hint: m[1].trim(),
    });
  }

  // 2. Find health-highlight ranges in the text (first occurrence each),
  // dropping any that overlap a SPECIFY chip so we don't double-decorate.
  const healthRanges = computeHighlightRanges(text, issues).filter(
    (r) => !placeholderRanges.some((p) => !(r.end <= p.start || r.start >= p.end)),
  );

  // 3. Weave into a single ordered list of spans.
  type DecoratedSegment =
    | { kind: 'placeholder'; start: number; end: number; full: string; hint: string }
    | { kind: 'highlight'; start: number; end: number; message: string };

  const boundaries: DecoratedSegment[] = [
    ...placeholderRanges.map((p): DecoratedSegment => ({ kind: 'placeholder', ...p })),
    ...healthRanges.map((h): DecoratedSegment => ({ kind: 'highlight', ...h })),
  ].sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  boundaries.forEach((seg, i) => {
    if (seg.start > cursor) {
      parts.push(
        <span key={`t-${cursor}`}>{text.slice(cursor, seg.start)}</span>,
      );
    }
    if (seg.kind === 'placeholder') {
      parts.push(
        <PlaceholderChip
          key={`p-${i}`}
          hint={seg.hint}
          fullMatch={seg.full}
          onReplace={onReplace}
          disabled={disabled}
        />,
      );
    } else {
      parts.push(
        <mark
          key={`h-${i}`}
          className="bg-yellow-100 border-b border-yellow-400 text-yellow-900 rounded-sm px-0.5"
          title={seg.message}
          data-testid="claim-health-highlight"
        >
          {text.slice(seg.start, seg.end)}
        </mark>,
      );
    }
    cursor = seg.end;
  });
  if (cursor < text.length) {
    parts.push(<span key={`t-end`}>{text.slice(cursor)}</span>);
  }

  return (
    <div
      className="whitespace-pre-wrap leading-loose"
      style={{ fontFamily: 'IBM Plex Serif, serif' }}
    >
      {parts}
    </div>
  );
}

function ClaimHealthBadge({
  result, claimNumber,
}: {
  result: HealthResult;
  claimNumber: number;
}) {
  const [open, setOpen] = useState(false);
  if (result.status === 'ok') {
    return (
      <span
        data-testid={`claim-health-ok-${claimNumber}`}
        title="No issues found"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-green-100 border border-green-200 text-green-800"
      >
        <Check size={10} /> OK
      </span>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`claim-health-warn-${claimNumber}`}
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setOpen(false)}
        title={
          result.issues
            .map((i) => `• ${i.message}`)
            .join('\n')
        }
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-amber-100 border border-amber-200 text-amber-800 hover:bg-amber-200 transition-colors"
      >
        <AlertTriangle size={10} />
        {result.issues.length} issue{result.issues.length === 1 ? '' : 's'}
      </button>
      {open && (
        <div
          data-testid={`claim-health-issues-${claimNumber}`}
          className="absolute right-0 mt-1 w-80 z-20 bg-white border border-amber-200 rounded-sm shadow-md p-2 text-xs text-zinc-800"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul className="space-y-1">
            {result.issues.map((issue, idx) => (
              <li key={idx} className="flex items-start gap-1.5">
                <span
                  className={
                    'mt-0.5 inline-block h-1.5 w-1.5 rounded-full ' +
                    (issue.severity === 'advisory' ? 'bg-amber-400' : 'bg-amber-600')
                  }
                />
                <span>
                  <span className="font-medium text-amber-900">{issue.check_label}:</span>{' '}
                  {issue.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Severities that force the findings panel open on mount so the
 * drafter can't miss a blocker. Everything else (INFO, advisory,
 * note) starts collapsed. Lowercase set because upstream severity
 * strings arrive in mixed case ("Critical", "BLOCKER", "major"). */
const _AUTO_EXPAND_SEVERITIES = new Set([
  'blocker', 'major', 'critical', 'error',
]);


function ClaimQcFindings({
  claimNumber, findings,
}: {
  claimNumber: number;
  findings: Array<{
    claim_number: number;
    category?: string;
    severity?: string;
    message?: string;
    suggested_fix?: string;
  }>;
}) {
  const serious = React.useMemo(
    () => findings.some(f => _AUTO_EXPAND_SEVERITIES.has((f.severity || '').toLowerCase())),
    [findings],
  );
  // Reset the open/closed default whenever the findings array
  // identity changes (a new QC run produces a new list, even with
  // the same count). Using JSON.stringify keeps the deps deterministic
  // without having to deep-compare per-field.
  const findingsKey = React.useMemo(
    () => JSON.stringify(findings.map(f => [f.severity, f.message, f.suggested_fix])),
    [findings],
  );
  const [open, setOpen] = React.useState(serious);
  React.useEffect(() => {
    setOpen(serious);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingsKey]);

  return (
    <div
      data-testid={`qc-findings-${claimNumber}`}
      className="mx-4 mt-3 border border-amber-200 rounded-sm bg-amber-50/60"
    >
      <button
        type="button"
        data-testid={`qc-findings-toggle-${claimNumber}`}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-amber-50"
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-amber-900 inline-flex items-center gap-2">
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          QC findings · {findings.length}
          {serious && (
            <span className="text-[9px] font-medium text-red-800 bg-red-100 border border-red-200 rounded-sm px-1.5 py-0.5">
              needs review
            </span>
          )}
        </span>
        <span className="text-[10px] text-amber-800">
          {open ? 'Collapse' : 'Expand'}
        </span>
      </button>
      {open && (
        <ul className="space-y-1.5 px-3 pb-3">
          {findings.map((f, i) => {
            const sev = (f.severity || '').toLowerCase();
            const tone = sev === 'error' || sev === 'blocker' || sev === 'critical'
              ? 'text-red-700'
              : sev === 'major' || sev === 'warn' || sev === 'warning'
                ? 'text-amber-800'
                : 'text-zinc-700';
            return (
              <li key={i} className="text-[11px] flex items-start gap-1.5">
                <span className={`font-mono uppercase text-[9px] shrink-0 mt-0.5 ${tone}`}>
                  {sev || 'info'}
                </span>
                <div className="flex-1">
                  <div className="text-zinc-800">{f.message}</div>
                  {f.suggested_fix && (
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      Suggested fix: {f.suggested_fix}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


export function ClaimCard({ claim, matterId, onUpdate, readOnly, healthResult, qcFindings }: {
  claim: Claim; matterId: string; onUpdate: () => void; readOnly?: boolean;
  healthResult: HealthResult;
  qcFindings: Array<{
    claim_number: number | null;
    category?: string;
    severity?: string;
    message?: string;
    suggested_fix?: string;
  }>;
}) {
  // `readOnly` is set when Stage 3 is rendered in review mode from Stage 4.
  // The claim itself may not be frozen, but we should still lock everything.
  const locked = !!readOnly || claim.is_frozen;
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(claim.claim_text);
  const [editAnalysis, setEditAnalysis] = useState(claim.analysis);
  // Set once per card: accepting a proposal that fills an empty
  // analysis must not snap the section open or shut under the drafter.
  const [analysisOpen, setAnalysisOpen] = useState(() => analysisStartsOpen(claim));
  // The whole claim set, for the steer proposal's antecedent check.
  const allClaims = useAppStore((s) => s.matterState?.claims);
  // Manual-edit mode — set when the drafter clicks Unfreeze on a
  // frozen claim. Forces the textarea visible, surfaces the amber
  // "QC will flag changes" notice, and swaps the Freeze button for
  // [Save & Re-freeze] + [Cancel]. Cleared on either of those two
  // actions completing successfully. Tracked independently of
  // editMode so a normal click-to-edit flow on an already-unfrozen
  // claim doesn't accidentally trip the Save & Re-freeze plumbing.
  const [manualEditMode, setManualEditMode] = useState(false);
  const [originalText, setOriginalText] = useState<string>(claim.claim_text);
  const [regenerating, setRegenerating] = useState(false);
  const [scopeLoading, setScopeLoading] = useState<string | null>(null);
  const [freezingClaim, setFreezingClaim] = useState(false);
  const [pendingRegen, setPendingRegen] = useState<any>(null);
  // Stage 3 Steer state — see docs/STAGE3_STEER_DESIGN.md. Mirrors the
  // pendingRegen pattern but adds the inline-textarea entry mode and
  // the dependent-aware preview shape.
  const [steerMode, setSteerMode] = useState<boolean>(false);
  const [steerInput, setSteerInput] = useState<string>('');
  const [steerLoading, setSteerLoading] = useState<boolean>(false);
  const [steerApplying, setSteerApplying] = useState<boolean>(false);
  const [pendingSteer, setPendingSteer] = useState<api.SteerPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [restoringIdx, setRestoringIdx] = useState<number | null>(null);
  const historyRef = React.useRef<HTMLDivElement | null>(null);
  // Scroll the history panel into view when it opens. The panel is
  // rendered below the claim text + analysis, which on a long claim
  // sits way below the viewport — clicking History made the button
  // appear to do nothing because the panel was off-screen.
  useEffect(() => {
    if (showHistory && historyRef.current) {
      historyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showHistory]);
  const diffRef = React.useRef<HTMLDivElement | null>(null);
  // Same for a Regenerate / Broaden / Narrow / Steer proposal: its diff
  // panel renders at the bottom of the card, so without the scroll the
  // button seemed to do nothing.
  useEffect(() => {
    if ((pendingRegen || pendingSteer) && diffRef.current) {
      diffRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [pendingRegen, pendingSteer]);
  // Two-step delete — mirrors the Home / Stage 1 delete UX.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingClaim, setDeletingClaim] = useState(false);

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeletingClaim(true);
    setError(null);

    // BUG-018 B4 — preview the cascade up-front instead of letting
    // the DELETE's 409 surface it post-hoc. Drafter sees every
    // claim the cascade will sweep BEFORE clicking Confirm, so a
    // multi-claim cascade can't surprise them.
    try {
      const preview = await api.previewDeleteClaim(claim.id);

      if (preview.frozen_dependents && preview.frozen_dependents.length > 0) {
        const frozenList = preview.frozen_dependents
          .map((d) => `Claim ${d.claim_number}`).join(', ');
        setError(
          `Cannot delete: ${frozenList} `
          + `${preview.frozen_dependents.length === 1 ? 'is' : 'are'} frozen. `
          + 'Unfreeze ' + (preview.frozen_dependents.length === 1 ? 'it' : 'them')
          + ' first, then retry.',
        );
        setDeletingClaim(false);
        setConfirmDelete(false);
        return;
      }

      const amended = preview.amended || [];
      if (preview.dependent_count > 0 || amended.length > 0) {
        const confirmed = window.confirm(
          deletePlanConfirmText(claim.claim_number, preview.dependents, amended),
        );
        if (!confirmed) {
          setError(null);
          setDeletingClaim(false);
          setConfirmDelete(false);
          return;
        }
        // Re-fire with confirm=true so the backend skips its own
        // 409 gate and runs the cascade. The preview's already
        // shown the drafter what's about to happen.
        try {
          await api.deleteClaim(claim.id, true);
          onUpdate();
        } catch (cascadeErr: any) {
          const cascadeDetail = cascadeErr?.response?.data?.detail;
          if (cascadeDetail?.error === 'frozen_dependents') {
            setError(
              cascadeDetail.message
              || 'Cannot delete: one or more dependent claims are frozen. Unfreeze them first.',
            );
          } else {
            setError(
              (typeof cascadeDetail === 'string' && cascadeDetail)
              || cascadeDetail?.message
              || 'Cascade delete failed. Please try again.',
            );
          }
        }
        // Reset here too: the early return skips the reset at the end,
        // which left a failed cascade's button spinning and disabled.
        setDeletingClaim(false);
        setConfirmDelete(false);
        return;
      }

      // Leaf claim — no dependents. Skip the confirm modal entirely
      // and fire the DELETE inline.
      await api.deleteClaim(claim.id);
      onUpdate();
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      // Fallback: if the preview endpoint hasn't been deployed yet
      // OR returned a non-404 error, drop into the prior
      // optimistic-DELETE-then-handle-409 path so the drafter
      // isn't locked out by a server-side rollout lag.
      const errKind = (detail && typeof detail === 'object')
        ? (detail.error
          || (detail.needs_confirmation ? 'has_dependents' : null))
        : null;

      if (status === 409 && errKind === 'has_dependents') {
        const confirmed = window.confirm(
          deletePlanConfirmText(
            claim.claim_number,
            Array.isArray(detail.dependents) ? detail.dependents : [],
            Array.isArray(detail.amended) ? detail.amended : [],
          ),
        );
        if (!confirmed) {
          setError(null);
          setDeletingClaim(false);
          setConfirmDelete(false);
          return;
        }
        try {
          await api.deleteClaim(claim.id, true);
          onUpdate();
        } catch (cascadeErr: any) {
          const cascadeDetail = cascadeErr?.response?.data?.detail;
          if (cascadeDetail?.error === 'frozen_dependents') {
            setError(
              cascadeDetail.message
              || 'Cannot delete: one or more dependent claims are frozen. Unfreeze them first.',
            );
          } else {
            setError(
              (typeof cascadeDetail === 'string' && cascadeDetail)
              || cascadeDetail?.message
              || 'Cascade delete failed. Please try again.',
            );
          }
        }
      } else if (status === 409 && errKind === 'frozen_dependents') {
        setError(
          detail.message
          || 'Cannot delete: one or more dependent claims are frozen. Unfreeze them first.',
        );
      } else {
        // Other errors (frozen independent claim, Stage 3 frozen,
        // network failure). ``detail`` may be a string or an object
        // — surface message preferentially, fall back to a generic
        // copy so a "[object Object]" never leaks into the toast.
        setError(
          (typeof detail === 'string' && detail)
          || detail?.message
          || 'Delete failed',
        );
      }
    }
    setDeletingClaim(false);
    setConfirmDelete(false);
  };

  const history = Array.isArray(claim.version_history) ? claim.version_history : [];

  const handleRestore = async (idx: number) => {
    setRestoringIdx(idx);
    setError(null);
    try {
      await api.restoreClaimVersion(claim.id, idx);
      setShowHistory(false);
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Restore failed'));
    }
    setRestoringIdx(null);
  };

  const handleSaveText = async (newText: string) => {
    try {
      await api.updateClaim(claim.id, { claim_text: newText });
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Save failed'));
    }
  };

  const handleReplacePlaceholder = async (original: string, replacement: string) => {
    const newText = editText.replace(original, replacement);
    setEditText(newText);
    await handleSaveText(newText);
  };

  const handleSaveAnalysis = async () => {
    try {
      await api.updateClaim(claim.id, { analysis: editAnalysis });
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Save failed'));
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setError(null);
    try {
      await api.updateClaim(claim.id, { analysis: editAnalysis });
      const result = await api.regenerateClaim(claim.id);
      setPendingRegen(result);
    } catch (e: any) {
      setError(apiErrorText(e, 'Regeneration failed'));
    }
    setRegenerating(false);
  };

  const handleAcceptRegen = async () => {
    if (!pendingRegen) return;
    try {
      await api.acceptRegeneration(claim.id, {
        claim_text: pendingRegen.new_claim_text,
        analysis: pendingRegen.new_analysis,
      });
      setEditText(pendingRegen.new_claim_text);
      // The server merges the new analysis over the old, so do the
      // same here; a proposal that omits a field must not blank its box.
      setEditAnalysis({ ...editAnalysis, ...(pendingRegen.new_analysis || {}) });
      setPendingRegen(null);
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Accept failed'));
    }
  };

  const handleScope = async (direction: string) => {
    setScopeLoading(direction);
    setError(null);
    try {
      const result = await api.adjustScope(claim.id, direction);
      setPendingRegen({
        new_claim_text: result.new_claim_text,
        new_analysis: result.new_analysis,
        change_summary: result.change_summary,
      });
    } catch (e: any) {
      setError(apiErrorText(e, 'Scope adjustment failed'));
    }
    setScopeLoading(null);
  };

  // ─── Stage 3 Steer — free-text, dependency-aware editing ─────
  // handleSteer fires the preview LLM call; on success the result is
  // stashed in pendingSteer and ClaimDiffPanel renders below the
  // claim. handleAcceptSteer commits primary + accepted dependents
  // atomically via /steer/accept (errors handled with the typed
  // steer_dep_stale / steer_dep_frozen error_codes from the design).

  const handleSteer = async () => {
    const instruction = steerInput.trim();
    if (!instruction) return;
    setSteerLoading(true);
    setError(null);
    try {
      const preview = await api.steerClaim(claim.id, instruction);
      setPendingSteer(preview);
      // Collapse the input panel once the preview is rendered — the
      // drafter now interacts with the diff panel.
      setSteerMode(false);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      // Backend wraps 422 refusals inside {error_code, detail}; surface
      // the plain string if present. apiErrorText is only the last
      // fallback (timeouts, HTML error pages): its sanitiser would
      // collapse the long steer refusals to generic copy.
      setError(
        typeof detail === 'string'
          ? detail
          : (typeof detail?.detail === 'string' && detail.detail) || apiErrorText(e, 'Steer failed'),
      );
    } finally {
      setSteerLoading(false);
    }
  };

  const handleAcceptSteer = async (acceptedDependentIds: string[]) => {
    if (!pendingSteer) return;
    setSteerApplying(true);
    setError(null);
    try {
      const acceptedUpdates = pendingSteer.affected_dependents
        .filter((d) => acceptedDependentIds.includes(d.id))
        .map((d) => ({ claim_id: d.id, modified_text: d.suggested_text }));
      await api.acceptSteer(claim.id, {
        modified_text: pendingSteer.modified,
        accepted_dependent_updates: acceptedUpdates,
      });
      setEditText(pendingSteer.modified);
      setPendingSteer(null);
      setSteerInput('');
      onUpdate();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : (typeof detail?.detail === 'string' && detail.detail) || apiErrorText(e, 'Apply failed'),
      );
    } finally {
      setSteerApplying(false);
    }
  };

  const handleDiscardSteer = () => {
    setPendingSteer(null);
    setSteerInput('');
  };

  const handleFreeze = async () => {
    setFreezingClaim(true);
    try {
      await api.freezeClaim(claim.id);
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Cannot freeze claim'));
    }
    setFreezingClaim(false);
  };

  // ── Per-claim manual-edit lifecycle ──────────────────────────
  // Unfreeze → flip claim.is_frozen=false via the explicit
  // /unfreeze endpoint AND set local manualEditMode=true so the
  // UI surfaces the dedicated [Save & Re-freeze] + [Cancel]
  // workflow + amber notice, rather than dropping back into the
  // generic click-to-edit flow.
  const handleUnfreezeForManualEdit = async () => {
    setFreezingClaim(true);
    setError(null);
    try {
      await api.unfreezeClaim(matterId, claim.id);
      setOriginalText(claim.claim_text);
      setEditText(claim.claim_text);
      setEditMode(true);
      setManualEditMode(true);
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Cannot unfreeze claim'));
    } finally {
      setFreezingClaim(false);
    }
  };

  // Save & Re-freeze → PUT text + flip claim.is_frozen=true via
  // the toggle freeze endpoint (currently unfrozen → freezes).
  // No QC re-run, no Stage 3 stage-level changes; the amber notice
  // makes the trade-off explicit ("QC will flag changes on next
  // review").
  const handleSaveRefreezeManualEdit = async () => {
    if (!editText.trim()) {
      setError('Cannot save empty claim text.');
      return;
    }
    setFreezingClaim(true);
    setError(null);
    try {
      await api.updateClaim(claim.id, { claim_text: editText });
      await api.freezeClaim(claim.id);
      setManualEditMode(false);
      setEditMode(false);
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Save & Re-freeze failed'));
    } finally {
      setFreezingClaim(false);
    }
  };

  // Cancel manual edit → revert local editText to the snapshot
  // taken at Unfreeze time and re-freeze. Best-effort PATCH first
  // so the server-side text matches what we just reverted to.
  const handleCancelManualEdit = async () => {
    setFreezingClaim(true);
    setError(null);
    try {
      setEditText(originalText);
      try { await api.updateClaim(claim.id, { claim_text: originalText }); } catch { /* tolerate */ }
      await api.freezeClaim(claim.id);
      setManualEditMode(false);
      setEditMode(false);
      onUpdate();
    } catch (e: any) {
      setError(apiErrorText(e, 'Cancel failed'));
    } finally {
      setFreezingClaim(false);
    }
  };

  const isIndependent = claim.claim_type === 'independent';
  const fill = analysisFill(editAnalysis, isIndependent);
  const analysisHint = fill.filled === 0 ? 'empty' : `${fill.filled} of ${fill.total} filled`;

  // Under a steer proposal: the dependents it would leave pointing at
  // wording this claim no longer introduces. Follows the dependent
  // checkboxes, since a ticked suggestion may fix the reference.
  const steerAntecedentNotice = (ticked: string[]) => {
    if (!pendingSteer) return null;
    const suggested = pendingSteer.affected_dependents || [];
    const breaks = antecedentBreaksAfterSteer(
      claim.id, pendingSteer.modified, allClaims || [],
      suggested.filter((d) => ticked.includes(d.id)),
    );
    const note = steerAntecedentNote(
      claim.claim_number,
      breaks,
      suggested.filter((d) => !ticked.includes(d.id)).map((d) => d.claim_number),
    );
    if (!note) return null;
    return (
      <div
        role="status"
        data-testid={`steer-antecedent-note-${claim.claim_number}`}
        className="mt-4 p-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900 flex items-start gap-1.5"
      >
        <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-600" />
        <span>{note}</span>
      </div>
    );
  };

  // Broaden / Narrow are disabled while a steer proposal is waiting;
  // say so rather than leave the buttons silently greyed out.
  const scopeBlockedTitle = pendingSteer
    ? 'Accept or discard the proposed change below first'
    : undefined;

  return (
    <div
      data-testid={`claim-card-${claim.claim_number}`}
      className={`border rounded-sm bg-white mb-4 transition-all ${claim.is_frozen ? 'border-green-200 bg-green-50/20' : 'border-zinc-200'}`}
      id={`claim-${claim.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50/50">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold text-zinc-900">Claim {claim.claim_number}</span>
          {/* Capitalised in code: CSS capitalize would turn "Dependent
              on claim 1 or 2" into "Dependent On Claim 1 Or 2". */}
          <span
            data-testid={`claim-type-${claim.claim_number}`}
            className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-600"
          >
            {claimTypeLabel(claim)}
          </span>
          <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-600 capitalize">{claim.category}</span>
          {claim.placeholder_count > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 border border-amber-200 rounded-sm text-xs text-amber-800 font-mono flex items-center gap-1">
              <Hash size={10} />
              {claim.placeholder_count}
            </span>
          )}
          {claim.is_frozen && (
            <span className="px-2 py-0.5 bg-green-100 border border-green-200 rounded-sm text-xs text-green-800 flex items-center gap-1">
              <Lock size={10} /> Frozen
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ClaimHealthBadge result={healthResult} claimNumber={claim.claim_number} />
          {isIndependent && !locked && (
            <>
              <button
                data-testid={`broaden-claim-${claim.claim_number}`}
                onClick={() => handleScope('Broaden')}
                disabled={!!scopeLoading || steerLoading || !!pendingSteer}
                title={scopeBlockedTitle}
                className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors disabled:opacity-50"
              >
                {scopeLoading === 'Broaden' ? <Loader2 size={12} className="animate-spin" /> : <Maximize2 size={12} />}
                Broaden
              </button>
              <button
                data-testid={`narrow-claim-${claim.claim_number}`}
                onClick={() => handleScope('Narrow')}
                disabled={!!scopeLoading || steerLoading || !!pendingSteer}
                title={scopeBlockedTitle}
                className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors disabled:opacity-50"
              >
                {scopeLoading === 'Narrow' ? <Loader2 size={12} className="animate-spin" /> : <Minimize2 size={12} />}
                Narrow
              </button>
              <button
                data-testid={`steer-claim-${claim.claim_number}`}
                onClick={() => setSteerMode((s) => !s)}
                disabled={steerLoading || !!scopeLoading || !!pendingRegen || !!pendingSteer}
                title={
                  steerDisabledReason(pendingRegen, pendingSteer)
                  || 'Free-text steer — describe the change in plain English'
                }
                className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors disabled:opacity-50"
              >
                {steerLoading ? <Loader2 size={12} className="animate-spin" /> : <Compass size={12} />}
                Steer
              </button>
            </>
          )}
          {history.length > 0 && (
            <button
              data-testid={`claim-history-${claim.claim_number}`}
              onClick={() => setShowHistory(h => !h)}
              className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm transition-colors"
              title={`${history.length} previous version${history.length !== 1 ? 's' : ''} retained`}
            >
              <HistoryIcon size={12} /> History ({history.length})
            </button>
          )}
          {/* Freeze / Unfreeze toggle.
              - If the claim is editable (not frozen, not review-mode): show
                "Freeze" (disabled while placeholders remain).
              - If the claim is frozen but Stage 3 itself is still open
                (drafter navigated here normally or unfroze Stage 3 from
                Stage 4): show "Unfreeze" so they can tweak it. The backend
                endpoint is already a toggle — same call, new label.
              - If Stage 3 is being viewed read-only from Stage 4 review,
                hide both (readOnly=true). */}
          {/* In manual-edit mode the Freeze / Unfreeze pair is
              replaced by dedicated [Save & Re-freeze] + [Cancel]
              buttons so the drafter sees a single, explicit
              "what does this commit" affordance. */}
          {!readOnly && manualEditMode && (
            <>
              <button
                data-testid={`save-refreeze-claim-${claim.claim_number}`}
                onClick={handleSaveRefreezeManualEdit}
                disabled={freezingClaim || !editText.trim()}
                className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50 transition-colors"
              >
                {freezingClaim ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
                Save &amp; Re-freeze
              </button>
              <button
                data-testid={`cancel-edit-claim-${claim.claim_number}`}
                onClick={handleCancelManualEdit}
                disabled={freezingClaim}
                className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
          {!readOnly && !manualEditMode && !claim.is_frozen && (
            <button
              data-testid={`freeze-claim-${claim.claim_number}`}
              onClick={handleFreeze}
              disabled={claim.placeholder_count > 0 || freezingClaim}
              title={freezeButtonTitle(claim.placeholder_count)}
              className={`flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium transition-colors ${
                claim.placeholder_count > 0 ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed' : 'bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-green-50 hover:text-green-800'
              }`}
            >
              {freezingClaim ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
              Freeze
            </button>
          )}
          {!readOnly && !manualEditMode && claim.is_frozen && (
            <button
              data-testid={`unfreeze-claim-${claim.claim_number}`}
              onClick={handleUnfreezeForManualEdit}
              disabled={freezingClaim}
              className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
              title="Unfreeze for manual editing — QC will flag changes on next review"
            >
              {freezingClaim ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Unfreeze
            </button>
          )}
          {!locked && (
            <button
              data-testid={`delete-claim-${claim.claim_number}`}
              onClick={handleDelete}
              onBlur={() => setConfirmDelete(false)}
              disabled={deletingClaim}
              title={confirmDelete ? 'Click again to confirm' : 'Delete this claim'}
              className={`flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium transition-colors ${
                confirmDelete
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-zinc-100 text-zinc-500 border border-zinc-200 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              {deletingClaim
                ? <Loader2 size={12} className="animate-spin" />
                : <Trash2 size={12} />}
              {confirmDelete && !deletingClaim ? 'Confirm' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          data-testid={`claim-error-${claim.claim_number}`}
          className="px-4 py-2 bg-red-50 text-sm text-red-800 flex items-center gap-1"
        >
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* QC findings — shown only when /stage_4/qc has been run and
          returned findings for this claim_number. Collapsible per
          claim so a 10-finding INFO-only set doesn't push the rest
          of the card off-screen. Auto-expanded when at least one
          finding is BLOCKER / MAJOR / CRITICAL / ERROR severity
          (the drafter must see those). Otherwise starts collapsed.
          Collapse state is local (not persisted) and resets each
          time the findings array identity changes — i.e. a new
          QC run rewrites the defaults. */}
      {qcFindings.length > 0 && (
        <ClaimQcFindings
          claimNumber={claim.claim_number}
          findings={qcFindings}
        />
      )}

      {/* Claim text */}
      <div className="p-4" data-testid={`claim-text-${claim.claim_number}`}>
        {/* Amber notice — only in manual-edit mode. Sits above
            the textarea so the drafter sees the "QC will flag
            changes" warning before they start typing. */}
        {manualEditMode && (
          <div
            data-testid={`claim-manual-edit-notice-${claim.claim_number}`}
            className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900 flex items-start gap-1.5"
          >
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-600" />
            <span>
              Manual edit mode — QC will flag changes on next review.
              Other claims and the stage-level frozen state are
              unchanged.
            </span>
          </div>
        )}
        {locked ? (
          <ClaimBodyRenderer
            text={claim.claim_text}
            issues={healthResult.issues}
            onReplace={() => {}}
            disabled={true}
          />
        ) : (editMode || manualEditMode) ? (
          <textarea
            data-testid={`claim-text-edit-${claim.claim_number}`}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={() => {
              // In manual-edit mode the textarea stays open until
              // the drafter clicks Save & Re-freeze or Cancel —
              // auto-save on blur would persist half-typed edits
              // and the explicit-action contract is the whole
              // point of the spec. Outside manual-edit, behave
              // exactly as before.
              if (manualEditMode) return;
              handleSaveText(editText);
              setEditMode(false);
            }}
            rows={10}
            className="w-full bg-white border border-zinc-300 rounded-sm px-4 py-3 text-sm leading-loose focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
            autoFocus
          />
        ) : (
          <div
            onClick={() => !locked && setEditMode(true)}
            className="cursor-text hover:bg-zinc-50/50 transition-colors rounded-sm p-2 -m-2 min-h-[60px]"
          >
            <ClaimBodyRenderer
              text={editText}
              issues={healthResult.issues}
              onReplace={handleReplacePlaceholder}
              disabled={locked}
            />
          </div>
        )}
      </div>

      {/* Analysis — below the claim text and collapsible: on dependents
          the four boxes were mostly empty and pushed the claim itself
          down the page. Regenerate stays in the header row so it works
          while collapsed. */}
      <div
        className="mx-4 mb-4 border border-zinc-200 rounded-sm bg-zinc-50/30"
        data-testid={`analysis-block-${claim.claim_number}`}
      >
        <div className="flex items-center justify-between px-3 py-2">
          <button
            type="button"
            data-testid={`analysis-toggle-${claim.claim_number}`}
            onClick={() => setAnalysisOpen((v) => !v)}
            aria-expanded={analysisOpen}
            aria-controls={`analysis-body-${claim.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-[#002FA7] transition-colors"
          >
            {analysisOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Analysis
            <span
              data-testid={`analysis-hint-${claim.claim_number}`}
              className="normal-case tracking-normal text-[11px] text-zinc-400"
            >
              · {analysisHint}
            </span>
          </button>
          {!locked && (
            <button
              data-testid={`regenerate-claim-${claim.claim_number}`}
              onClick={handleRegenerate}
              disabled={regenerating}
              className="text-xs text-[#002FA7] hover:text-[#002277] flex items-center gap-1 transition-colors"
            >
              {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Regenerate from analysis
            </button>
          )}
        </div>
        {/* hidden on a wrapper: on the grid itself Tailwind's display
            utility would override it. */}
        <div
          id={`analysis-body-${claim.id}`}
          data-testid={`analysis-body-${claim.claim_number}`}
          hidden={!analysisOpen}
        >
          <div className="grid grid-cols-2 gap-3 px-3 pb-3">
            {isIndependent && (
              <div className="col-span-2">
                <label className="text-xs text-zinc-500 block mb-1">Distinguishing Features</label>
                <textarea
                  data-testid={`analysis-dist-features-${claim.claim_number}`}
                  value={editAnalysis.distinguishing_features || ''}
                  onChange={(e) => setEditAnalysis({ ...editAnalysis, distinguishing_features: e.target.value })}
                  onBlur={handleSaveAnalysis}
                  disabled={locked}
                  rows={3}
                  className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[4rem]"
                />
              </div>
            )}
            <div className="col-span-2">
              <label className="text-xs text-zinc-500 block mb-1">Key Terms</label>
              <textarea
                data-testid={`analysis-key-terms-${claim.claim_number}`}
                value={Array.isArray(editAnalysis.key_terms) ? editAnalysis.key_terms.join(', ') : editAnalysis.key_terms || ''}
                onChange={(e) => setEditAnalysis({ ...editAnalysis, key_terms: e.target.value.split(',').map((s: string) => s.trim()) })}
                onBlur={handleSaveAnalysis}
                disabled={locked}
                rows={2}
                className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[3rem]"
                placeholder="comma-separated"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-zinc-500 block mb-1">Technical Effect</label>
              <textarea
                data-testid={`analysis-tech-effect-${claim.claim_number}`}
                value={editAnalysis.technical_effect || ''}
                onChange={(e) => setEditAnalysis({ ...editAnalysis, technical_effect: e.target.value })}
                onBlur={handleSaveAnalysis}
                disabled={locked}
                rows={2}
                className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[3rem]"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-zinc-500 block mb-1">Advantage</label>
              <textarea
                data-testid={`analysis-advantage-${claim.claim_number}`}
                value={editAnalysis.advantage || ''}
                onChange={(e) => setEditAnalysis({ ...editAnalysis, advantage: e.target.value })}
                onBlur={handleSaveAnalysis}
                disabled={locked}
                rows={2}
                className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50 resize-y min-h-[3rem]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Version history */}
      {showHistory && history.length > 0 && (
        <div
          ref={historyRef}
          data-testid={`claim-history-panel-${claim.claim_number}`}
          className="mx-4 mb-4 border border-zinc-200 rounded-sm bg-zinc-50/50 p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Previous versions (last {history.length}, newest first)
            </span>
            <button
              onClick={() => setShowHistory(false)}
              className="text-xs text-zinc-400 hover:text-zinc-600"
            >Close</button>
          </div>
          <div className="space-y-2">
            {history.slice().reverse().map((v: any, reverseIdx: number) => {
              // history.slice().reverse() inverts order; original index = length - 1 - reverseIdx
              const origIdx = history.length - 1 - reverseIdx;
              return (
                <div
                  key={`${v.generated_at}-${origIdx}`}
                  data-testid={`claim-history-item-${claim.claim_number}-${origIdx}`}
                  className="border border-zinc-200 rounded-sm bg-white p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-500">
                      {v.generated_at ? new Date(v.generated_at).toLocaleString() : 'unknown time'}
                    </span>
                    {!locked && (
                      <button
                        data-testid={`restore-version-${claim.claim_number}-${origIdx}`}
                        onClick={() => handleRestore(origIdx)}
                        disabled={restoringIdx === origIdx}
                        className="text-xs flex items-center gap-1 px-2 py-1 bg-[#002FA7] text-white rounded-sm hover:bg-[#002277] disabled:opacity-50"
                      >
                        {restoringIdx === origIdx ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Restore
                      </button>
                    )}
                  </div>
                  <div
                    className="text-xs text-zinc-700 leading-relaxed max-h-32 overflow-y-auto whitespace-pre-wrap"
                    style={{ fontFamily: 'IBM Plex Serif, serif' }}
                  >
                    {v.claim_text}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Steer input — inline textarea, shown when the Steer button toggled it open */}
      {steerMode && !pendingSteer && (
        <div
          data-testid={`steer-input-${claim.claim_number}`}
          className="mx-4 mb-3 border border-blue-200 rounded-sm bg-blue-50/30 p-3"
        >
          <label
            htmlFor={`steer-textarea-${claim.id}`}
            className="text-[10px] uppercase tracking-wider text-blue-700 mb-1 block font-mono"
          >
            Steering instruction
          </label>
          <textarea
            id={`steer-textarea-${claim.id}`}
            data-testid={`steer-textarea-${claim.claim_number}`}
            value={steerInput}
            onChange={(e) => setSteerInput(e.target.value)}
            placeholder="e.g. Add a limitation requiring the actuators to be synchronous. Remove the reference to Bluetooth — keep the genus. Convert the preamble to an apparatus claim."
            rows={3}
            disabled={steerLoading}
            className="w-full text-sm bg-white border border-zinc-300 rounded-sm p-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:opacity-60"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
            maxLength={2000}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-zinc-500 font-mono">
              {steerInput.length} / 2000
            </span>
            <div className="flex gap-2">
              <button
                data-testid={`steer-submit-${claim.claim_number}`}
                onClick={handleSteer}
                disabled={!steerInput.trim() || steerLoading}
                className="bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50 disabled:cursor-not-allowed rounded-sm px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1"
              >
                {steerLoading ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                Apply steering
              </button>
              <button
                data-testid={`steer-cancel-${claim.claim_number}`}
                onClick={() => { setSteerMode(false); setSteerInput(''); }}
                disabled={steerLoading}
                className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div ref={diffRef}>
        {/* Pending regeneration / scope (Narrow / Broaden / Regenerate) — */}
        {/* uses the shared ClaimDiffPanel with no dependents. */}
        {pendingRegen && (
          <ClaimDiffPanel
            testIdPrefix={`regen-preview-${claim.claim_number}`}
            original={claim.claim_text}
            modified={pendingRegen.new_claim_text}
            changeSummary={pendingRegen.change_summary}
            affectedDependents={[]}
            onApply={handleAcceptRegen}
            onDiscard={() => setPendingRegen(null)}
            applyLabel="Accept new"
          />
        )}

        {/* Pending steer — same panel; dependent block self-renders */}
        {/* when affected_dependents is non-empty. */}
        {pendingSteer && (
          <ClaimDiffPanel
            testIdPrefix={`steer-preview-${claim.claim_number}`}
            original={pendingSteer.original}
            modified={pendingSteer.modified}
            changeSummary={pendingSteer.changes_summary}
            affectedDependents={pendingSteer.affected_dependents}
            onApply={handleAcceptSteer}
            onDiscard={handleDiscardSteer}
            applyLabel="Apply changes"
            applyPending={steerApplying}
            renderNotice={steerAntecedentNotice}
          />
        )}
      </div>
    </div>
  );
}

function AddClaimModal({ matterId, preset, claims, onClose, onAdded }: {
  matterId: string;
  preset: { kind: 'independent' | 'dependent'; parent_claim_number: number | null };
  claims: Claim[];
  onClose: () => void;
  onAdded: () => void;
}) {
  // Drafter-facing form for POST /matter/{id}/claim/add. The sidebar "+ Add"
  // buttons seed kind + parent; the modal lets the drafter change them and
  // write a plain-English instruction that the LLM turns into a new claim.
  const [kind, setKind] = useState<'independent' | 'dependent'>(preset.kind);
  const [parent, setParent] = useState<number | null>(preset.parent_claim_number);
  const [category, setCategory] = useState<string>(() => {
    // Default category: parent's category for deps, 'method' for new indeps.
    if (preset.kind === 'dependent' && preset.parent_claim_number) {
      const p = claims.find(c => c.claim_number === preset.parent_claim_number);
      if (p) return p.category;
    }
    return 'method';
  });
  const [instruction, setInstruction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const independents = claims
    .filter(c => c.claim_type === 'independent')
    .sort((a, b) => a.claim_number - b.claim_number);

  const handleSubmit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await api.addClaim(matterId, {
        kind,
        category,
        parent_claim_number: kind === 'dependent' ? parent : null,
        instruction,
      });
      onAdded();
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to add claim'));
    }
    setSubmitting(false);
  };

  const canSubmit = kind === 'independent' || (kind === 'dependent' && !!parent);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm"
      data-testid="add-claim-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-sm border border-zinc-200 shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Add claim
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {err && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-center gap-1.5">
            <AlertTriangle size={14} /> {err}
          </div>
        )}

        <label className="text-xs text-zinc-500 block mb-1">Kind</label>
        <div className="flex gap-2 mb-4">
          {(['independent', 'dependent'] as const).map(k => (
            <button
              type="button"
              key={k}
              data-testid={`add-claim-kind-${k}`}
              onClick={() => setKind(k)}
              className={`flex-1 px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                kind === k ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]' : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
              }`}
            >
              {k === 'independent' ? 'New independent' : 'New dependent'}
            </button>
          ))}
        </div>

        {kind === 'dependent' && (
          <>
            <label className="text-xs text-zinc-500 block mb-1">Depends on</label>
            <select
              data-testid="add-claim-parent"
              value={parent ?? ''}
              onChange={(e) => {
                const num = e.target.value ? Number(e.target.value) : null;
                setParent(num);
                if (num) {
                  const p = claims.find(c => c.claim_number === num);
                  if (p) setCategory(p.category);
                }
              }}
              className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            >
              <option value="">Select an independent claim</option>
              {independents.map(ind => (
                <option key={ind.id} value={ind.claim_number}>
                  Claim {ind.claim_number} · {ind.category}
                </option>
              ))}
            </select>
          </>
        )}

        <label className="text-xs text-zinc-500 block mb-1">Category</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {['method', 'system', 'crm', 'apparatus'].map(cat => (
            <button
              type="button"
              key={cat}
              data-testid={`add-claim-category-${cat}`}
              onClick={() => setCategory(cat)}
              disabled={kind === 'dependent'}
              title={kind === 'dependent' ? 'Dependents inherit their parent\u2019s category' : undefined}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                category === cat
                  ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]'
                  : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
              } ${kind === 'dependent' ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>

        <label className="text-xs text-zinc-500 block mb-1">Instruction (optional)</label>
        <textarea
          data-testid="add-claim-instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={4}
          placeholder={kind === 'dependent'
            ? 'e.g. narrow by requiring the Kalman filter to update at 10 Hz'
            : 'e.g. add a system claim covering the sensor + processor combination'}
          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-y min-h-[5rem]"
          maxLength={600}
        />

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="add-claim-submit"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add claim
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Stage3() {
  const {
    matterState, setMatterState,
    readOnlyStage, setReadOnlyStage,
    scrollToClaimId, setScrollToClaimId,
    setActiveStage,
  } = useAppStore();
  const matter = matterState?.matter;
  const matterId = matter?.id || '';
  // Stabilise the claims array reference across renders so the
  // useMemo health map below doesn't re-run on every parent re-render.
  const claims = useMemo(
    () => matterState?.claims || [],
    [matterState?.claims],
  );
  const s3 = matterState?.stages?.stage_3;
  const s3Content = s3?.content || {};
  // Read-only when the stage is frozen OR when the user arrived via
  // "Jump to claim" from Stage 4.
  const isReviewMode = readOnlyStage === 'stage_3';
  const isFrozen = !!s3?.is_frozen || isReviewMode;

  const [generating, setGenerating] = useState(false);
  const [freezingStage, setFreezingStage] = useState(false);
  // Latest /claims/relink response — fuels the amber "still unlinked"
  // diagnostic under the Re-link button so the drafter knows which
  // claim texts to edit. Reset to [] whenever a fresh relink starts.
  const [relinkUnresolvable, setRelinkUnresolvable] = useState<
    import('@/api/client').RelinkUnresolvable[]
  >([]);
  // Stage 3 freeze response surfaces ep_multi_dep_warnings — non-
  // blocking advisory notes on EP multiple dependent claims the
  // drafter should resolve before filing.
  const [epMultiDepWarnings, setEpMultiDepWarnings] = useState<string[]>([]);
  // Stage 3 generate now returns soft validation warnings (the
  // RULE -1 category mismatch is the first emitter — locked
  // category vs what the LLM actually produced). Surfaced as an
  // amber banner so the drafter sees the issue but the generate
  // result lands instead of dying with a 502. Cleared on every
  // fresh generate.
  const [generationWarnings, setGenerationWarnings] = useState<
    api.Stage3GenerationWarning[]
  >([]);
  // A dropped connection is not a failed generation. Held apart from
  // `error` so the UI can say what actually happened and offer to go
  // and look, instead of blaming the model settings.
  const [genTimeout, setGenTimeout] = useState<string | null>(null);
  const [genPhase, setGenPhase] = useState<
    'queued' | 'running' | 'recovering' | null
  >(null);
  const [checkingSaved, setCheckingSaved] = useState(false);
  // The claim ids as they were before the current generate. Generation
  // replaces every claim with a new row, so a changed id set is how we
  // tell "it finished after the connection dropped" from "it never
  // ran" — without it, a recovery check would just re-show the
  // previous run's claims.
  const claimIdsBeforeGen = useRef<string[] | null>(null);
  // epi compliance Item 2 — review acknowledgment gates the
  // "Continue to QC" button. AND-ed with canFreezeStage below.
  const persistedAckS3: string | null = (s3Content as any)?.acknowledged_at || null;
  const [ackAtS3, setAckAtS3] = useState<string | null>(persistedAckS3);
  useEffect(() => { setAckAtS3(persistedAckS3); }, [persistedAckS3]);
  const [activeClaimId, setActiveClaimId] = useState<string | null>(
    scrollToClaimId || claims[0]?.id || null,
  );
  const [error, setError] = useState<string | null>(null);

  // Collapsed sidebar state — persisted in localStorage so the preference
  // sticks across refreshes / matter switches. Default is expanded.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('stage3.sidebarCollapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('stage3.sidebarCollapsed', sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);

  // Add-claim modal state. preset carries the kind + parent hint from the
  // sidebar "+ Add …" button; the drafter can edit them in the dialog before
  // submitting.
  const [addClaimPreset, setAddClaimPreset] = useState<null | {
    kind: 'independent' | 'dependent';
    parent_claim_number: number | null;
  }>(null);

  // Scroll to a claim after mount if Stage 4 asked us to, then clear the
  // signal so subsequent navigations don't re-trigger it.
  useEffect(() => {
    if (!scrollToClaimId) return;
    setActiveClaimId(scrollToClaimId);
    // Give the DOM a tick to render then scroll into view.
    const t = setTimeout(() => {
      const el = document.getElementById(`claim-${scrollToClaimId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
        }, 2500);
      }
      setScrollToClaimId(null);
    }, 120);
    return () => clearTimeout(t);
  }, [scrollToClaimId, setScrollToClaimId]);

  // Select a claim and scroll its card into view. Used by the sidebar
  // and by the claim numbers in the gate banner.
  const jumpToClaim = (id: string) => {
    setActiveClaimId(id);
    // Scroll the main pane to the claim card — without this the
    // sidebar tree highlights the row but the page sits still and
    // the drafter has to hunt for it on long claim sets.
    requestAnimationFrame(() => {
      const el = document.getElementById(`claim-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Flash a ring so the jump destination is obvious.
        el.classList.add('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-[#002FA7]', 'ring-offset-2');
        }, 1600);
      }
    });
  };

  const refreshMatter = useCallback(async () => {
    const data = await api.getMatter(matterId);
    setMatterState(data);
  }, [matterId, setMatterState]);

  const handleGenerate = async () => {
    // Concurrency guard — the empty-state gate banner and any
    // auto-generation effect both route here, so a rapid double-
    // click (or a race between auto-fire and a user click) would
    // otherwise trigger two POSTs to /claims/generate. setGenerating
    // is the single flag both paths check.
    if (generating) return;
    setGenerating(true);
    setError(null);
    setGenTimeout(null);
    setGenPhase(null);
    // Reset warnings on every fresh attempt so a stale banner
    // from a prior run doesn't shadow the current result.
    setGenerationWarnings([]);
    claimIdsBeforeGen.current = (claims || [])
      .map((c: any) => c?.id)
      .filter(Boolean);
    try {
      const result = await api.generateClaimsResilient(matterId, {
        onPhase: setGenPhase,
      });
      // Soft warnings (category_mismatch etc.) — surface in the
      // amber banner below without blocking the success path.
      if (result?.warnings?.length) {
        setGenerationWarnings(result.warnings);
      }
      if (result?.recovered_from_timeout) {
        setGenTimeout(null);
        setError(null);
      }
      await refreshMatter();
    } catch (e: any) {
      if (e?.name === 'GenerationTimeoutError') {
        // The work is very likely still running or already done. The
        // one thing not to do here is tell the drafter to go and
        // change LLM settings that are working.
        setGenTimeout(
          e.message
          || 'The connection dropped while the claims were being generated.',
        );
        setGenerating(false);
        setGenPhase(null);
        return;
      }
      // BUG-005 B3 — backend now returns a structured 400 with
      // detail = { error_code, message, missing_precondition }
      // when Stage 2.5 strategy isn't frozen. Surface the `message`
      // string only — never render the raw object (which would
      // print '[object Object]' in the error banner).
      const detail = e?.response?.data?.detail;
      let msg: string;
      if (detail && typeof detail === 'object') {
        if (detail.error_code === 'claim_strategy_required') {
          msg = 'Claim strategy required — confirm a strategy in the Strategy tab before generating claims.';
        } else {
          msg = typeof detail.message === 'string' ? detail.message : 'Claim generation failed. Check LLM settings.';
        }
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (typeof e?.message === 'string' && e.message) {
        msg = e.message;
      } else {
        msg = 'Claim generation failed. Check LLM settings.';
      }
      setError(msg);
    }
    setGenerating(false);
    setGenPhase(null);
  };

  /** "Check if it saved" — the drafter's way to settle a dropped
   * connection without paying for another four-minute run. */
  const handleCheckClaimsSaved = async () => {
    setCheckingSaved(true);
    try {
      const saved = await api.checkClaimsSaved(
        matterId, claimIdsBeforeGen.current,
      );
      if (saved) {
        setGenTimeout(null);
        setError(null);
        await refreshMatter();
      } else {
        setGenTimeout(
          'Still nothing new saved. The server may still be working — '
          + 'wait a moment and check again, or generate again.',
        );
      }
    } finally {
      setCheckingSaved(false);
    }
  };

  const handleFreezeStage = async () => {
    setFreezingStage(true);
    setError(null);
    try {
      const resp = await api.freezeStage3(matterId);
      // EP multiple-dependency notes — surface mixed-category multi-dep
      // warnings before advancing the drafter to Stage 4 so they
      // can decide whether to unfreeze + fix or accept the trade-
      // off. Non-blocking; freeze already succeeded server-side.
      setEpMultiDepWarnings(resp?.ep_multi_dep_warnings || []);
      await refreshMatter();
      // Auto-advance to Stage 4 so "Continue to QC" actually continues
      // — previously the click froze the stage but left the drafter
      // on the post-freeze banner, requiring a second click in the
      // top-bar stage chip to move forward.
      setActiveStage('stage_4');
    } catch (e: any) {
      setError(apiErrorText(e, 'Cannot freeze Stage 3'));
    }
    setFreezingStage(false);
  };

  // Run-QC in-place. The existing /stage_4/qc endpoint runs matter-wide
  // QC and stores findings under stage_3.content.qc_findings indexed by
  // claim_number, so we can surface them right on each ClaimCard here
  // without jumping into Stage 4. Drafters iterate faster this way:
  // run QC → fix → regenerate → run QC again.
  const [runningQc, setRunningQc] = useState(false);
  const [qcResultMessage, setQcResultMessage] = useState<string | null>(null);
  // QC reads the frozen claim set: the server refuses it (422
  // stage_3_not_frozen) until Stage 3 is frozen, so the button waits too.
  const qcBlockedReason = qcRunBlockedReason(!!s3?.is_frozen, claims.length);
  const handleRunQc = async () => {
    if (qcBlockedReason) {
      setError(qcBlockedReason);
      return;
    }
    setRunningQc(true);
    setQcResultMessage(null);
    setError(null);
    try {
      const res = await api.runQc(matterId);
      await refreshMatter();
      const n = Array.isArray(res?.findings) ? res.findings.length : 0;
      // Split per-claim vs category-wide so the banner is honest
      // about where the drafter has to look. An LLM that returns
      // claim_number=0 for every finding would otherwise say "see
      // claim cards below" even though every finding rendered in
      // the General card at the top.
      let perClaim = 0;
      let general = 0;
      for (const f of res?.findings || []) {
        const raw = (f as any)?.claim_number;
        let cn: number | null = null;
        if (typeof raw === 'number' && Number.isFinite(raw)) cn = Math.trunc(raw);
        else if (typeof raw === 'string') {
          const m = raw.match(/\d+/);
          if (m) { const p = parseInt(m[0], 10); if (Number.isFinite(p)) cn = p; }
        }
        if (cn !== null && cn > 0) perClaim++; else general++;
      }
      if (n === 0) {
        setQcResultMessage('QC pass: no findings.');
      } else if (perClaim > 0 && general > 0) {
        setQcResultMessage(
          `QC found ${n} finding${n === 1 ? '' : 's'} — ${perClaim} on specific claims, ${general} general.`,
        );
      } else if (perClaim > 0) {
        setQcResultMessage(`QC found ${n} finding${n === 1 ? '' : 's'} — see claim cards below.`);
      } else {
        setQcResultMessage(`QC found ${n} finding${n === 1 ? '' : 's'} — see the General QC card below.`);
      }
    } catch (e: any) {
      setError(apiErrorText(e, 'QC run failed.'));
    }
    setRunningQc(false);
  };

  // "Freeze All Claims" — bulk-freeze every claim with zero placeholders
  // without advancing to Stage 4. The drafter then clicks Continue to QC
  // separately, which hits the original freezeStage3 gate.
  const [freezingAll, setFreezingAll] = useState(false);
  const [bulkFreezeResult, setBulkFreezeResult] = useState<null | {
    frozen: number; skipped_with_placeholders: number[];
  }>(null);
  const handleFreezeAllClaims = async () => {
    setFreezingAll(true);
    setError(null);
    setBulkFreezeResult(null);
    try {
      const result = await api.freezeAllClaims(matterId);
      setBulkFreezeResult({
        frozen: Number(result?.frozen) || 0,
        skipped_with_placeholders: Array.isArray(result?.skipped_with_placeholders)
          ? result.skipped_with_placeholders : [],
      });
      await refreshMatter();
    } catch (e: any) {
      setError(apiErrorText(e, 'Failed to freeze all claims'));
    }
    setFreezingAll(false);
  };

  // ─── Claim health evaluation ────────────────────────────────────
  // Memoised keyed on the claim texts so typing in the edit box doesn't
  // re-compute for every claim on every keystroke (edit happens inside
  // ClaimCard's local state and only syncs to claims[] on save).
  const idfTexts = useMemo(() => idfTextsFromState(matterState), [matterState]);
  const healthByClaimId = useMemo(() => {
    const map = new Map<string, HealthResult>();
    for (const c of claims) {
      map.set(c.id, evaluateClaimHealth(c, claims, { idfTexts }));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, idfTexts]);

  // QC findings come back from /stage_4/qc and live on
  // stage_3.content.qc_findings. Bucket them by claim_number for
  // per-card rendering and keep a separate list of claim_number-0
  // (category-wide) findings that render as a top-level card.
  // Anthropic and GPT-4o both occasionally emit numeric-string
  // claim_numbers or descriptive strings despite the prompt asking
  // for plain int — handle those defensively here too.
  const { qcFindingsByClaimNumber, qcGeneralFindings } = useMemo(() => {
    const map = new Map<number, any[]>();
    const general: any[] = [];
    const findings = (matterState?.stages?.stage_3?.content as any)?.qc_findings;
    if (Array.isArray(findings)) {
      for (const f of findings) {
        const raw = f?.claim_number;
        let n: number | null = null;
        if (typeof raw === 'number' && Number.isFinite(raw)) {
          n = Math.trunc(raw);
        } else if (typeof raw === 'string' && raw.trim()) {
          const match = raw.match(/\d+/);
          if (match) {
            const parsed = parseInt(match[0], 10);
            if (Number.isFinite(parsed)) n = parsed;
          }
        }
        if (n === null || n <= 0) {
          general.push(f);
          continue;
        }
        if (!map.has(n)) map.set(n, []);
        map.get(n)!.push(f);
      }
    }
    return { qcFindingsByClaimNumber: map, qcGeneralFindings: general };
  }, [matterState]);

  if (claims.length === 0) {
    // Two distinct empty states:
    //   1. Stage 2 NOT frozen — the drafter shouldn't be here yet.
    //      Keep the red gate banner: this is a genuine blocked
    //      precondition, not a welcome screen.
    //   2. Stage 2 frozen, no claims yet — the normal first-visit
    //      path. Show a clean welcome card in the neutral/blue
    //      palette used elsewhere. Previously we reused the red
    //      StageGateBanner for this which made a fresh matter
    //      look like an error state.
    const stage2Frozen = !!(matterState?.stages as any)?.stage_2?.is_frozen;
    // BUG-005 B2 — Stage 2.5 strategy must be frozen before the
    // backend's claim_strategy_required gate (server.py:14963)
    // will let Stage 3 generate proceed. Read the column directly
    // off the stage_2_5 row; matches the precedent at Stage2.tsx:660.
    const stage25StrategyFrozen = !!(matterState?.stages as any)?.stage_2_5?.claim_strategy_frozen;
    const jurisdiction = matter?.jurisdiction || '';

    if (!stage2Frozen) {
      return (
        <div className="max-w-3xl mx-auto pt-20 pb-12 px-6">
          <h1
            data-testid="stage-3-title"
            className="text-3xl font-semibold tracking-tight text-zinc-900 mb-1"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Stage 3 — Claim Drafting
          </h1>
          <p className="text-sm text-zinc-500 mb-4">Generate and refine patent claims</p>
          <StageGateBanner
            testId="stage-3-gate-banner"
            status="blocked"
            message="Freeze Stage 2 (Novelty & Inventive Step) before drafting claims."
            className="mb-4"
          />
        </div>
      );
    }

    // Welcome / first-visit state. Neutral card, blue primary button,
    // zero red. Error banner only surfaces beneath the card when a
    // previous Generate attempt failed.
    return (
      <div className="max-w-3xl mx-auto pt-16 pb-12 px-6">
        <h1
          data-testid="stage-3-title"
          className="text-3xl font-semibold tracking-tight text-zinc-900 mb-1"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Stage 3 — Claim Drafting
        </h1>
        <p className="text-sm text-zinc-500 mb-6">Generate and refine patent claims</p>

        {!stage25StrategyFrozen && (
          <div
            data-testid="stage-3-strategy-required-banner"
            className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900 flex items-start gap-2"
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span className="flex-1">
              A claim strategy is needed before claims can be drafted — lock the
              strategy (recommended) or skip it, in the Strategy tab of Stage 2.
            </span>
            <button
              type="button"
              // Fix: was ``onJumpToStage('stage_2_5')`` but
              // ``onJumpToStage`` isn't a prop / in-scope name
              // inside Stage3 — that name belongs to
              // Stage3Sidebar. The corresponding handler at
              // line ~2004 (handleJumpToStage) is declared after
              // this JSX's lexical position, so it can't be
              // referenced here (use-before-decl). Calling
              // setActiveStage directly is equivalent for this
              // jump-back affordance (the handler's
              // setReadOnlyStage(null) branch is unreachable
              // when we're already in editable Stage 3).
              onClick={() => setActiveStage('stage_2_5')}
              className="text-[#002FA7] hover:underline font-medium"
              data-testid="stage-3-go-to-stage-2-5"
            >
              Go to Strategy
            </button>
          </div>
        )}

        <div
          data-testid="stage-3-welcome"
          className="border border-zinc-200 rounded-sm bg-white p-8 text-center space-y-4"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-[#002FA7] mx-auto">
            <Zap size={20} />
          </div>
          <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Ready to draft claims
          </h2>
          <p className="text-sm text-zinc-600 max-w-md mx-auto leading-relaxed">
            Your Novelty and Inventive Step analysis is complete.
            Generate your initial claim set to get started.
          </p>
          <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest">
            Jurisdiction: <span className="text-zinc-700">{jurisdiction || '—'}</span>
          </div>
          <button
            data-testid="stage-3-welcome-generate"
            onClick={handleGenerate}
            disabled={generating || !stage25StrategyFrozen}
            title={!stage25StrategyFrozen ? 'Lock or skip the claim strategy (Stage 2, Strategy tab) first.' : undefined}
            className="mt-2 bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-5 py-2.5 text-sm font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {generating
              ? 'Generating claims…'
              : error ? 'Retry' : 'Generate Initial Claim Set'}
          </button>
          {generating && (
            <p className="text-[11px] text-zinc-500 italic"
               data-testid="stage3-generating-note">
              {genPhase === 'recovering'
                ? 'The connection dropped — checking whether the server '
                  + 'finished anyway.'
                : genPhase === 'queued'
                  ? 'Queued on the server. You can leave this page; the run '
                    + 'continues without the browser.'
                  : genPhase === 'running'
                    ? 'Running on the server. You can leave this page; the '
                      + 'run continues without the browser.'
                    : 'This can take several minutes on a long disclosure.'}
            </p>
          )}
        </div>

        {/* A dropped connection, told apart from a failure. The claims
            are usually already written by the time the browser gives
            up, so the offer is to go and look rather than to pay for
            another run. */}
        {genTimeout && !generating && (
          <div
            data-testid="stage3-gen-timeout"
            className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <div>{genTimeout}</div>
                <div className="mt-1 text-[12px] text-amber-800">
                  Generating again costs another full run. Check first.
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="stage3-check-saved"
                    disabled={checkingSaved}
                    onClick={handleCheckClaimsSaved}
                    className="rounded-sm border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                  >
                    {checkingSaved ? 'Checking…' : 'Check if it saved'}
                  </button>
                  <button
                    type="button"
                    data-testid="stage3-generate-again"
                    disabled={generating}
                    onClick={handleGenerate}
                    className="rounded-sm border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                  >
                    Generate again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && !generating && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  const independentClaims = claims.filter(c => c.claim_type === 'independent');
  const allIndependentFrozen = independentClaims.every(c => c.is_frozen);
  const totalPlaceholders = claims.reduce((sum, c) => sum + c.placeholder_count, 0);
  const canFreezeStage = allIndependentFrozen && totalPlaceholders === 0;

  // Jump-back handler for sidebar STAGES list. Only frozen / earlier stages
  // should be clickable; Stage3Sidebar already enforces that via accessible.
  const handleJumpToStage = (s: Stage) => {
    if (s === 'stage_3') return;
    setReadOnlyStage(null);
    setActiveStage(s);
  };

  return (
    <div className="h-screen pt-14 flex flex-col overflow-hidden bg-white" data-testid="stage-3-layout">
      <div className="flex flex-1 overflow-hidden">
      <Stage3Sidebar
        claims={claims}
        activeClaimId={activeClaimId}
        onSelect={jumpToClaim}
        matterState={matterState}
        onJumpToStage={handleJumpToStage}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(v => !v)}
        onAddClaimFor={(preset) => setAddClaimPreset(preset)}
        readOnly={isFrozen}
        healthByClaimId={healthByClaimId}
        qcFindingsByClaimNumber={qcFindingsByClaimNumber}
        onRelink={async () => {
          // Best-effort: surface any backend error in the
          // existing stage-level error banner; the relink
          // endpoint is idempotent so a transient failure
          // doesn't leave the matter in a bad state.
          try {
            const resp = await api.relinkClaims(matterId);
            setRelinkUnresolvable(resp.unresolvable || []);
            await refreshMatter();
          } catch (e: any) {
            setError(apiErrorText(e, 'Re-link failed'));
          }
        }}
        relinkUnresolvable={relinkUnresolvable}
      />
      {addClaimPreset && (
        <AddClaimModal
          matterId={matterId}
          preset={addClaimPreset}
          claims={claims}
          onClose={() => setAddClaimPreset(null)}
          onAdded={async () => {
            setAddClaimPreset(null);
            await refreshMatter();
          }}
        />
      )}
      <div className="flex-1 overflow-y-auto p-8 md:p-12">
        {/* Width clamp: keep the 4xl line length when the sidebar is
            expanded (readable paragraph width), but drop to full
            width when the sidebar is collapsed so the drafter's
            explicit "give me more room" gesture actually produces
            more room. QC findings and long claim text benefit most
            from the extra horizontal real estate. */}
        <div className={sidebarCollapsed ? 'w-full' : 'max-w-4xl'}>
          {!isFrozen && (() => {
            // Gate banner computed at the stage-level so the drafter
            // sees a single top-of-page summary in the same visual
            // language used in Stage 1/2/4. Tier-2 QC findings =
            // ``critical`` severity on any persisted finding; they
            // surface as a warning (can proceed, should review)
            // rather than a hard block so the drafter can ignore and
            // freeze anyway if they disagree with the LLM's call.
            const unfrozenIndepsForBanner = independentClaims.filter(c => !c.is_frozen).length;
            const qcCriticalCount = [
              ...qcGeneralFindings,
              ...Array.from(qcFindingsByClaimNumber.values()).flat(),
            ].filter(f => {
              const s = (f?.severity || '').toString().toLowerCase();
              return s === 'critical' || s === 'tier_2' || s === 'tier2';
            }).length;
            if (unfrozenIndepsForBanner > 0 || totalPlaceholders > 0) {
              // Unfrozen claims are the normal state while drafting, so
              // this is an amber reminder, not a red error; the Continue
              // to QC button carries the hard gate.
              return (
                <StageGateBanner
                  testId="stage-3-gate-banner"
                  status="warning"
                  message={
                    <ClaimGateMessage
                      unfrozen={independentClaims.filter(c => !c.is_frozen)}
                      withPlaceholders={claims.filter(c => c.placeholder_count > 0)}
                      onJump={jumpToClaim}
                    />
                  }
                  className="mb-4"
                />
              );
            }
            if (qcCriticalCount > 0) {
              return (
                <StageGateBanner
                  testId="stage-3-gate-banner"
                  status="warning"
                  message={`Your last QC found ${qcCriticalCount} structural issue${qcCriticalCount === 1 ? '' : 's'}. Review them, then click Continue to QC and run QC again in Stage 4.`}
                  className="mb-4"
                />
              );
            }
            return null;
          })()}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 data-testid="stage-3-title" className="text-2xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Claim Drafting
              </h1>
              <p className="text-sm text-zinc-500">{claims.length} claims · {totalPlaceholders} placeholder{totalPlaceholders !== 1 ? 's' : ''}</p>
            </div>
            {!isFrozen && (() => {
              // Two-button layout (drafter request): a stand-alone
              // "Freeze All Claims" that bulk-freezes every clean claim
              // without advancing, plus a separate "Continue to QC" that
              // is the original gate (all independents frozen + zero
              // placeholders across the set, PRD §8.6).
              const unfrozenClaims = claims.filter(c => !c.is_frozen);
              const unfrozenWithoutPlaceholders = unfrozenClaims.filter(
                c => c.placeholder_count === 0,
              );
              const unfrozenIndeps = independentClaims.filter(c => !c.is_frozen);
              const reasons: string[] = [];
              if (unfrozenIndeps.length > 0) {
                reasons.push(
                  `${unfrozenIndeps.length} independent claim${unfrozenIndeps.length === 1 ? '' : 's'} not frozen ` +
                  `(${unfrozenIndeps.map(c => '#' + c.claim_number).join(', ')})`,
                );
              }
              if (totalPlaceholders > 0) {
                reasons.push(
                  `${totalPlaceholders} unresolved placeholder${totalPlaceholders === 1 ? '' : 's'} remaining ` +
                  `(${claims.filter(c => c.placeholder_count > 0).map(c => '#' + c.claim_number).join(', ')})`,
                );
              }
              const blocked = reasons.join(' · ');
              const canBulkFreeze = unfrozenWithoutPlaceholders.length > 0;
              return (
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="stage-3-run-qc-button"
                      onClick={handleRunQc}
                      disabled={runningQc || !!qcBlockedReason}
                      title={qcBlockedReason || 'Run the QC review. Findings show inline on each claim card.'}
                      className="rounded-sm px-3 py-2 text-sm font-medium transition-colors flex items-center gap-2 border bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      {runningQc ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                      {runningQc ? 'Running QC…' : 'Run QC'}
                    </button>
                    <button
                      data-testid="freeze-all-claims-button"
                      onClick={handleFreezeAllClaims}
                      disabled={!canBulkFreeze || freezingAll}
                      title={
                        canBulkFreeze
                          ? `Freeze ${unfrozenWithoutPlaceholders.length} clean claim${unfrozenWithoutPlaceholders.length === 1 ? '' : 's'} in one click. Claims with placeholders are skipped.`
                          : 'Nothing to freeze — every clean claim is already frozen'
                      }
                      className={`rounded-sm px-3 py-2 text-sm font-medium transition-colors flex items-center gap-2 border ${
                        canBulkFreeze
                          ? 'bg-white text-[#002FA7] border-[#002FA7] hover:bg-blue-50'
                          : 'bg-zinc-50 text-zinc-400 border-zinc-200 cursor-not-allowed'
                      }`}
                    >
                      {freezingAll ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                      Freeze All Claims
                    </button>
                    <button
                      data-testid="freeze-stage-3-button"
                      onClick={handleFreezeStage}
                      disabled={!canFreezeStage || !ackAtS3 || freezingStage}
                      title={
                        blocked
                          || (!ackAtS3 ? 'Check the review-acknowledged box to continue' : 'All gates clear — freeze Stage 3 and go to Stage 4, where you run QC')
                      }
                      className={`rounded-sm px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
                        canFreezeStage && ackAtS3 ? 'bg-[#002FA7] text-white hover:bg-[#002277]' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                      }`}
                    >
                      {freezingStage ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                      Continue to QC
                    </button>
                  </div>
                  {/* epi compliance Item 2 — review acknowledgment
                      gates the "Continue to QC" button above. The
                      checkbox lives on its own row so the "Freeze
                      All Claims" bulk action keeps its visual
                      association with the Continue button. */}
                  <StageAcknowledgment
                    matterId={matterId}
                    stage="stage_3"
                    initialAcknowledgedAt={ackAtS3}
                    onChange={setAckAtS3}
                    disabled={!!isFrozen}
                    onError={setError}
                  />
                  {bulkFreezeResult && (
                    <span
                      data-testid="bulk-freeze-result"
                      className={`text-[11px] rounded-sm px-2 py-0.5 border ${
                        bulkFreezeResult.skipped_with_placeholders.length > 0
                          ? 'text-amber-800 bg-amber-50 border-amber-200'
                          : 'text-green-800 bg-green-50 border-green-200'
                      }`}
                    >
                      Froze {bulkFreezeResult.frozen} claim{bulkFreezeResult.frozen === 1 ? '' : 's'}
                      {bulkFreezeResult.skipped_with_placeholders.length > 0 &&
                        ` · skipped ${bulkFreezeResult.skipped_with_placeholders.length} with placeholders ` +
                        `(${bulkFreezeResult.skipped_with_placeholders.map(n => '#' + n).join(', ')})`}
                    </span>
                  )}
                  {blocked && (
                    <span
                      data-testid="freeze-stage-3-blocked-reason"
                      className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5"
                    >
                      Continue to QC blocked: {blocked}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
              <AlertTriangle size={16} /> {error}
            </div>
          )}
          {epMultiDepWarnings.length > 0 && (
            <div
              data-testid="ep-multi-dep-warnings"
              className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800"
            >
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle size={16} />
                {/* Not "Rule 43(2)": that rule governs multiple
                    independent claims in one category. The notes
                    below cite what they actually rest on. */}
                {epMultiDepWarnings.length} note
                {epMultiDepWarnings.length === 1 ? '' : 's'} on multiple dependent claims — review before filing
              </div>
              <ul className="mt-2 list-disc ml-6 space-y-0.5 text-xs">
                {epMultiDepWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Soft validation warnings from Stage 3 generate.
              Category mismatch ("you locked system but Claim 1
              came back as method") is currently the only emitter;
              the shape is open so future non-fatal validators
              can land here without UI changes. Dismiss button
              clears the banner without affecting the underlying
              data — the claims are already persisted. */}
          {generationWarnings.length > 0 && (
            <div
              data-testid="stage3-generation-warnings"
              className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 font-medium">
                  <AlertTriangle size={16} />
                  {generationWarnings.length === 1
                    ? 'Generation warning'
                    : `${generationWarnings.length} generation warnings`}
                </div>
                <button
                  type="button"
                  className="text-xs text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline"
                  onClick={() => setGenerationWarnings([])}
                  aria-label="Dismiss generation warnings"
                  data-testid="stage3-generation-warnings-dismiss"
                >
                  Dismiss
                </button>
              </div>
              <ul className="mt-2 list-disc ml-6 space-y-0.5 text-xs">
                {generationWarnings.map((w, i) => (
                  <li key={`${w.code}-${i}`}>{w.message}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Claim-architecture review panel (plan Phase 4). Renders
              nothing when the feature flag is off or the pre-pass
              hasn't run for this matter, so it is inert on the
              unmodified path. Read-only stages hide it — an override
              would imply a regenerate the drafter can't perform. */}
          {!isFrozen && matterId && (
            <div className="mb-4">
              <ClaimArchitecturePanel
                matterId={matterId}
                hasClaims={claims.length > 0}
                onRequestRegenerate={handleGenerate}
              />
            </div>
          )}
          {qcResultMessage && (
            <div
              data-testid="stage-3-qc-result"
              className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800 flex items-center justify-between"
            >
              <span>{qcResultMessage}</span>
              <button
                type="button"
                onClick={() => setQcResultMessage(null)}
                className="text-blue-700 hover:text-blue-900"
                aria-label="Dismiss"
              >
                <X size={12} />
              </button>
            </div>
          )}
          {/* General QC findings — findings whose claim_number is 0
              (or missing / unparseable) don't tie to a specific card,
              so they render once at the top of the main pane. This
              catches the common LLM behaviour of returning
              category-wide issues without attaching them to a claim
              (especially Anthropic when the message describes a
              pattern across multiple claims). */}
          {qcGeneralFindings.length > 0 && (
            <div
              data-testid="stage-3-qc-general-findings"
              className="mb-4 border border-amber-200 rounded-sm bg-amber-50/60 p-3"
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-900 mb-1.5">
                General QC findings · {qcGeneralFindings.length}
              </div>
              <p className="text-[11px] text-zinc-600 mb-2">
                Findings that apply to the whole claim set, not a single claim.
              </p>
              <ul className="space-y-1.5">
                {qcGeneralFindings.map((f, i) => {
                  const sev = (f?.severity || '').toLowerCase();
                  const tone = sev === 'critical' || sev === 'error'
                    ? 'text-red-700'
                    : (sev === 'warning' || sev === 'warn')
                      ? 'text-amber-800'
                      : 'text-zinc-700';
                  return (
                    <li key={i} className="text-[11px] flex items-start gap-1.5">
                      <span className={`font-mono uppercase text-[9px] shrink-0 mt-0.5 ${tone}`}>
                        {sev || 'info'}
                      </span>
                      <div className="flex-1">
                        {f?.category && (
                          <span className="text-[10px] text-zinc-500 mr-1">[{f.category}]</span>
                        )}
                        <span className="text-zinc-800">{f?.message}</span>
                        {f?.suggested_fix && (
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            Suggested fix: {f.suggested_fix}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {isReviewMode && (
            <div
              data-testid="stage-3-review-banner"
              className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between text-sm text-blue-900"
            >
              <span className="flex items-center gap-2">
                <Eye size={16} /> Review mode — claims are read-only while you check QC findings.
              </span>
              <button
                data-testid="stage-3-exit-review"
                onClick={() => { setReadOnlyStage(null); setActiveStage('stage_4'); }}
                className="text-xs px-2 py-1 bg-white text-[#002FA7] border border-blue-200 rounded-sm hover:bg-blue-100 transition-colors"
              >
                Back to QC
              </button>
            </div>
          )}
          {/* !! because SQLite returns is_frozen as 0, which JSX renders. */}
          {!isReviewMode && !!s3?.is_frozen && (
            <div
              data-testid="stage-3-frozen-banner"
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-sm flex items-center justify-between gap-2 text-sm text-green-800"
            >
              <span className="flex items-center gap-2">
                <CheckCircle size={18} /> Stage 3 frozen — proceed to QC & Export
              </span>
              <button
                type="button"
                data-testid="stage-3-continue-to-stage-4"
                onClick={() => { setReadOnlyStage(null); setActiveStage('stage_4'); }}
                className="rounded-sm px-3 py-1.5 text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] inline-flex items-center gap-1.5"
              >
                Continue to QC &amp; Export <ArrowRight size={12} />
              </button>
            </div>
          )}
          {claims.map(claim => {
            const health = healthByClaimId.get(claim.id) || {
              claim_id: claim.id,
              status: 'ok' as const,
              issues: [],
              failed_checks: [],
            };
            return (
              <ClaimCard
                key={claim.id}
                claim={claim}
                matterId={matterId}
                onUpdate={refreshMatter}
                readOnly={isReviewMode}
                healthResult={health}
                qcFindings={qcFindingsByClaimNumber.get(claim.claim_number) || []}
              />
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
}

